import { useState, useEffect, useCallback } from 'react';
import * as audit from '@/services/audit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub, faBitbucket, faAtlassian, faJira, faTrello, faConfluence, faNotion, faGoogle, faGitlab, faSlack, faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import {
  faArrowLeft,
  faArrowRightArrowLeft,
  faArrowUpRightFromSquare,
  faBell,
  faBell as faBellSolid,
  faBellSlash,
  faBuilding,
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faCircleUser,
  faCodeCommit,
  faCopy,
  faDatabase,
  faEnvelope,
  faEye,
  faEyeSlash,
  faFileCode,
  faFolderOpen,
  faGear,
  faGears,
  faGlobe,
  faKey,
  faLink,
  faMinus,
  faPlug,
  faPlus,
  faRightFromBracket,
  faRotate,
  faShieldHalved,
  faSpinner,
  faToggleOff,
  faToggleOn,
  faTrash,
  faUser,
  faUsers,
  faWandMagicSparkles,
  faWifi,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import { atlassianOAuth2, getAtlassianRedirectUri } from '@/services/atlassian-oauth2';
import { githubOAuth2 } from '@/services/github-oauth2';
import { bitbucketOAuth2 } from '@/services/bitbucket-oauth2';
import { linearOAuth2 } from '@/services/linear-oauth2';
import { mondayOAuth2 } from '@/services/monday-oauth2';
import { getConfluenceSettings, saveConfluenceSettings, getAiSettings, saveAiSettings, clearAiSettings } from '@/services/storage';
import { AI_MODEL_OPTIONS } from '@/services/changelog-service';
import type { AtlassianOAuthState, PlatformOAuthState, AiSettings } from '@/services/types';
import { IndicatorConfigEditor } from './IndicatorConfigEditor';
import {
  getNotificationSettings, saveNotificationSettings, getPermissionState, requestNotificationPermission,
  NOTIFICATION_MODE_LABELS, type NotificationMode, type NotificationSettings
} from '@/services/notifications';
import * as api from '@/services/api';
import { validateLinearCredentials } from '@/services/linear-service';
import { validateMondayCredentials } from '@/services/monday-service';
import { validateTrelloCredentials } from '@/services/trello-service';
import { jiraTokenExpiryService, withJiraExpiryCheck, type JiraCredentials } from '@/services/jira-token-expiry';
import { getSlackSettings, saveSlackSettings, getTeamsSettings, saveTeamsSettings } from '@/services/messaging-storage';
import { slackService, type SlackChannelInfo } from '@/services/slack-service';
import { useJiraTokenExpiryModal } from '@/components/JiraTokenExpiryModal';
import {
  getStatusConfig,
  saveStatusConfig,
  resetStatusConfig,
  invalidateStatusCache,
  type JiraStatusConfig,
  type StatusCategory
} from '@/services/jira-status-config';

interface JiraSettings {
  enabled: boolean;
  domain: string;
  email: string;
  apiToken: string;
  defaultBoardId?: number;
  defaultProjectKey?: string;
  priorityUsers?: string[];
}

interface JiraUserOption {
  accountId: string;
  displayName: string;
  avatarUrl?: string;
  email?: string;
}

interface JiraProject {
  id: string;
  key: string;
  name: string;
}

interface JiraBoard {
  id: number;
  name: string;
  type: string;
  location?: {
    projectName: string;
    projectKey: string;
  };
}

interface SettingsViewProps {
  onClose: () => void;
  storedEmail?: string | null;
  storedToken?: string | null;
  storedUsername?: string | null;
  githubUsername?: string | null;
  githubToken?: string | null;
  onLogin?: (email: string, token: string, bbUsername?: string, ghUsername?: string, ghToken?: string) => Promise<void>;
  inline?: boolean;
  onNavigateToIndicatorEditor?: () => void;
  onSettingsChanged?: () => void;
}

const JIRA_SETTINGS_KEY = 'verigit-jira-settings';
const GIT_ALIASES_KEY = 'verigit-git-aliases';

export interface GitAlias {
  name: string;       // the alias/variant name (e.g. "Jane D")
  canonicalName: string; // the real person (e.g. "Jane Doe")
}

export function getStoredGitAliases(): GitAlias[] {
  try {
    const stored = localStorage.getItem(GIT_ALIASES_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

export function storeGitAliases(aliases: GitAlias[]): void {
  localStorage.setItem(GIT_ALIASES_KEY, JSON.stringify(aliases));
}

export function resolveGitAuthor(name: string, aliases: GitAlias[]): string {
  const lower = name.toLowerCase().trim();
  for (const alias of aliases) {
    if (alias.name.toLowerCase().trim() === lower) return alias.canonicalName;
  }
  return name;
}

function getStoredJiraSettings(): JiraSettings | null {
  try {
    const stored = localStorage.getItem(JIRA_SETTINGS_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
}

function storeJiraSettings(settings: JiraSettings): void {
  localStorage.setItem(JIRA_SETTINGS_KEY, JSON.stringify(settings));
}

function clearStoredJiraSettings(): void {
  localStorage.removeItem(JIRA_SETTINGS_KEY);
}

const PROFILE_NAME_KEY = 'verigit-profile-name';

function SecuritySection() {
  const [user, setUser] = useState<import('@/services/local-auth').LocalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [totpSecret, setTotpSecret] = useState('');
  const [totpQr, setTotpQr] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [step, setStep] = useState<'idle' | 'setup' | 'verify'>('idle');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    import('@/services/local-auth').then(async ({ getActiveSession, getUserById }) => {
      const session = await getActiveSession();
      if (session) {
        const u = await getUserById(session.userId);
        setUser(u);
      }
      setLoading(false);
    });
  }, []);

  const handleSetupMFA = async () => {
    if (!user) return;
    const { generateTOTPSecret, getTOTPUri } = await import('@/services/local-auth');
    const secret = generateTOTPSecret();
    const uri = getTOTPUri(secret, user.email);
    setTotpSecret(secret);
    const QRCode = (await import('qrcode')).default;
    const qr = await QRCode.toDataURL(uri);
    setTotpQr(qr);
    setStep('setup');
    setError(null);
  };

  const handleVerifyAndEnable = async () => {
    if (!user || !verifyCode.trim()) return;
    const { verifyTOTP, updateUser } = await import('@/services/local-auth');
    if (!verifyTOTP(totpSecret, verifyCode.replace(/\s/g, ''))) {
      setError('Invalid code — check your authenticator app and try again.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateUser(user.id, { mfaEnabled: true, mfaSecret: totpSecret });
      setUser(prev => prev ? { ...prev, mfaEnabled: true, mfaSecret: totpSecret } : prev);
      setStep('idle');
      setSuccess('Two-factor authentication enabled.');
      setVerifyCode('');
    } catch (e: any) {
      setError(e.message ?? 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDisableMFA = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      const { updateUser } = await import('@/services/local-auth');
      await updateUser(user.id, { mfaEnabled: false, mfaSecret: undefined });
      setUser(prev => prev ? { ...prev, mfaEnabled: false, mfaSecret: undefined } : prev);
      setSuccess('Two-factor authentication disabled.');
      setStep('idle');
    } catch (e: any) {
      setError(e.message ?? 'Failed to disable MFA.');
    } finally {
      setSaving(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(totpSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="flex items-center justify-center py-16"><FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!user) return <div className="py-8 text-sm text-slate-500 text-center">Sign in to manage security settings.</div>;

  const mfaEnabled = !!user.mfaSecret;

  return (
    <>
      <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Security</h2>
      <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Manage your account password and two-factor authentication</p>

      {/* MFA card */}
      <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden mb-4">
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${mfaEnabled ? 'bg-emerald-500/10' : 'bg-slate-500/10'}`}>
                <FontAwesomeIcon icon={faKey} className={`h-5 w-5 ${mfaEnabled ? 'text-emerald-500' : 'text-slate-400'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Authenticator App (TOTP)</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                  {mfaEnabled ? 'Two-factor authentication is active.' : 'Add an extra layer of security to your account.'}
                </p>
              </div>
            </div>
            {mfaEnabled ? (
              <button onClick={handleDisableMFA} disabled={saving} className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all disabled:opacity-50">
                {saving ? 'Disabling...' : 'Disable'}
              </button>
            ) : step === 'idle' ? (
              <button onClick={handleSetupMFA} className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-all">
                Enable
              </button>
            ) : null}
          </div>

          {/* Setup flow */}
          {step === 'setup' && (
            <div className="mt-5 space-y-4 border-t border-gray-100 dark:border-slate-700/50 pt-5">
              <p className="text-sm text-gray-700 dark:text-slate-300">Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)</p>
              {totpQr && <img src={totpQr} alt="TOTP QR code" className="w-44 h-44 rounded-xl border border-gray-200 dark:border-slate-700 mx-auto" />}
              <div>
                <p className="text-[10px] font-medium text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">Manual entry key</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono bg-gray-100 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 text-gray-800 dark:text-slate-200 break-all">{totpSecret}</code>
                  <button onClick={copySecret} className="p-2 rounded-lg border border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all" title="Copy key">
                    {copied ? <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 text-emerald-500" /> : <FontAwesomeIcon icon={faCopy} className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">Verify — enter the 6-digit code</label>
                <div className="flex gap-2">
                  <input type="text" inputMode="numeric" autoComplete="one-time-code" value={verifyCode} onChange={e => setVerifyCode(e.target.value)} placeholder="000 000" maxLength={7} className="flex-1 px-3 py-2 bg-white dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-sm font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500/50 transition-all" />
                  <button onClick={handleVerifyAndEnable} disabled={saving || !verifyCode.trim()} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm rounded-lg disabled:opacity-50 transition-all">
                    {saving ? 'Saving...' : 'Confirm'}
                  </button>
                  <button onClick={() => { setStep('idle'); setError(null); }} className="px-3 py-2 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white border border-gray-200 dark:border-slate-600 rounded-lg transition-all">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-3 flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
              <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-600 dark:text-red-300">{error}</p>
            </div>
          )}
          {success && (
            <div className="mt-3 flex items-start gap-2 p-2.5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-lg">
              <FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-600 dark:text-emerald-300">{success}</p>
            </div>
          )}
        </div>
      </div>

      {/* Account info */}
      <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faUser} className="h-5 w-5 text-indigo-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{user.displayName || user.email}</p>
            <p className="text-xs text-gray-500 dark:text-slate-400">{user.email}</p>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 dark:text-slate-500">
          To change your password, sign out and use the registration flow to create a new account, or contact your administrator.
        </p>
      </div>
    </>
  );
}

export function SettingsView({ onClose, storedEmail, storedToken: _storedToken, storedUsername, githubUsername: propGhUsername, githubToken: propGhToken, onLogin, inline, onNavigateToIndicatorEditor, onSettingsChanged }: SettingsViewProps) {

  // Token expiry modal
  const { Modal: TokenExpiryModal } = useJiraTokenExpiryModal();

  // Profile
  const [profileName, setProfileName] = useState(() => localStorage.getItem(PROFILE_NAME_KEY) || '');
  const [bbEmail, setBbEmail] = useState(storedEmail || '');
  const [bbToken, setBbToken] = useState(_storedToken || '');
  const [bbUsername, setBbUsername] = useState(storedUsername || '');
  const [showBbToken, setShowBbToken] = useState(false);
  const [bbValidating, setBbValidating] = useState(false);
  const [bbValidation, setBbValidation] = useState<{ valid: boolean; error?: string; username?: string; displayName?: string } | null>(
    storedEmail && _storedToken ? { valid: true } : null
  );
  const [ghToken, setGhToken] = useState(propGhToken || '');
  const [ghUsername, setGhUsername] = useState(propGhUsername || '');
  const [showGhToken, setShowGhToken] = useState(false);
  const [ghValidating, setGhValidating] = useState(false);
  const [ghValidation, setGhValidation] = useState<{ valid: boolean; error?: string; username?: string; displayName?: string } | null>(
    propGhToken ? { valid: true } : null
  );

  // Indicator config editor
  const [showIndicatorEditor, setShowIndicatorEditor] = useState(false);

  // Git alias state
  const [gitAliases, setGitAliases] = useState<GitAlias[]>([]);
  const [newAliasName, setNewAliasName] = useState('');
  const [newAliasCanonical, setNewAliasCanonical] = useState('');

  // Jira status config state
  const [statusConfig, setStatusConfig] = useState<JiraStatusConfig | null>(null);
  const [statusEditCategory, setStatusEditCategory] = useState<StatusCategory | null>(null);
  const [statusNewValue, setStatusNewValue] = useState('');
  const [statusSyncing, setStatusSyncing] = useState(false);
  const [statusSyncResult, setStatusSyncResult] = useState<string | null>(null);
  const [dragSource, setDragSource] = useState<{ category: StatusCategory; index: number; value: string } | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<StatusCategory | null>(null);

  // Export/Import profile state
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportResultModal, setShowImportResultModal] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [exportConfirmPassword, setExportConfirmPassword] = useState('');
  const [importPassword, setImportPassword] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [exportImportLoading, setExportImportLoading] = useState(false);
  const [exportImportError, setExportImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{ success: boolean; items?: string[]; error?: string } | null>(null);

  // Jira settings state
  const [jiraEnabled, setJiraEnabled] = useState(false);
  const [jiraDomain, setJiraDomain] = useState('');
  const [jiraEmail, setJiraEmail] = useState('');
  const [jiraApiToken, setJiraApiToken] = useState('');
  const [showJiraToken, setShowJiraToken] = useState(false);
  const [defaultBoardId, setDefaultBoardId] = useState<number | undefined>();
  const [defaultProjectKey, setDefaultProjectKey] = useState<string | undefined>();
  const [priorityUsers, setPriorityUsers] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<JiraUserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Validation / loading
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{ valid: boolean; error?: string; user?: any } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Boards / projects for defaults
  const [boards, setBoards] = useState<JiraBoard[]>([]);
  const [projects, setProjects] = useState<JiraProject[]>([]);

  // Linear settings state
  const [linearEnabled, setLinearEnabled] = useState(false);
  const [linearApiKey, setLinearApiKey] = useState('');
  const [showLinearKey, setShowLinearKey] = useState(false);
  const [linearValidating, setLinearValidating] = useState(false);
  const [linearValidation, setLinearValidation] = useState<{ valid: boolean; error?: string; user?: any } | null>(null);
  const [linearSaving, setLinearSaving] = useState(false);
  const [linearSaveSuccess, setLinearSaveSuccess] = useState(false);

  // Monday settings state
  const [mondayEnabled, setMondayEnabled] = useState(false);
  const [mondayApiKey, setMondayApiKey] = useState('');
  const [showMondayKey, setShowMondayKey] = useState(false);
  const [mondayValidating, setMondayValidating] = useState(false);
  const [mondayValidation, setMondayValidation] = useState<{ valid: boolean; error?: string; user?: any } | null>(null);
  const [mondaySaving, setMondaySaving] = useState(false);
  const [mondaySaveSuccess, setMondaySaveSuccess] = useState(false);

  // Confluence OAuth2 state
  const [confluenceOAuthState, setConfluenceOAuthState] = useState<AtlassianOAuthState | null>(null);
  const [confluenceEnabled, setConfluenceEnabled] = useState(false);
  const [confluenceClientId, setConfluenceClientId] = useState('');
  const [confluenceClientSecret, setConfluenceClientSecret] = useState('');
  const [showConfluenceSecret, setShowConfluenceSecret] = useState(false);
  const [confluenceConnecting, setConfluenceConnecting] = useState(false);
  const [confluenceError, setConfluenceError] = useState<string | null>(null);

  // ==================== Platform OAuth2 state (GitHub, Bitbucket, Linear, Monday) ====================
  const [ghOAuthState, setGhOAuthState] = useState<PlatformOAuthState | null>(null);
  const [ghOAuthClientId, setGhOAuthClientId] = useState('');
  const [ghOAuthClientSecret, setGhOAuthClientSecret] = useState('');
  const [showGhOAuthSecret, setShowGhOAuthSecret] = useState(false);
  const [ghOAuthConnecting, setGhOAuthConnecting] = useState(false);
  const [ghOAuthError, setGhOAuthError] = useState<string | null>(null);

  const [bbOAuthState, setBbOAuthState] = useState<PlatformOAuthState | null>(null);
  const [bbOAuthClientId, setBbOAuthClientId] = useState('');
  const [bbOAuthClientSecret, setBbOAuthClientSecret] = useState('');
  const [showBbOAuthSecret, setShowBbOAuthSecret] = useState(false);
  const [bbOAuthConnecting, setBbOAuthConnecting] = useState(false);
  const [bbOAuthError, setBbOAuthError] = useState<string | null>(null);

  const [linearOAuthState, setLinearOAuthState] = useState<PlatformOAuthState | null>(null);
  const [linearOAuthClientId, setLinearOAuthClientId] = useState('');
  const [linearOAuthClientSecret, setLinearOAuthClientSecret] = useState('');
  const [showLinearOAuthSecret, setShowLinearOAuthSecret] = useState(false);
  const [linearOAuthConnecting, setLinearOAuthConnecting] = useState(false);
  const [linearOAuthError, setLinearOAuthError] = useState<string | null>(null);

  const [mondayOAuthState, setMondayOAuthState] = useState<PlatformOAuthState | null>(null);
  const [mondayOAuthClientId, setMondayOAuthClientId] = useState('');
  const [mondayOAuthClientSecret, setMondayOAuthClientSecret] = useState('');
  const [showMondayOAuthSecret, setShowMondayOAuthSecret] = useState(false);
  const [mondayOAuthConnecting, setMondayOAuthConnecting] = useState(false);
  const [mondayOAuthError, setMondayOAuthError] = useState<string | null>(null);

  // GitLab settings state
  const [glInstanceUrl, setGlInstanceUrl] = useState('https://gitlab.com');
  const [glToken, setGlToken] = useState('');
  const [glUsername, setGlUsername] = useState('');
  const [showGlToken, setShowGlToken] = useState(false);
  const [glValidating, setGlValidating] = useState(false);
  const [glValidation, setGlValidation] = useState<{ valid: boolean; error?: string; username?: string } | null>(null);

  // Notion settings state
  const [notionEnabled, setNotionEnabled] = useState(false);
  const [notionApiKey, setNotionApiKey] = useState('');
  const [showNotionKey, setShowNotionKey] = useState(false);
  const [notionValidating, setNotionValidating] = useState(false);
  const [notionValidation, setNotionValidation] = useState<{ valid: boolean; error?: string; workspaceName?: string } | null>(null);
  const [notionSaving, setNotionSaving] = useState(false);
  const [notionSaveSuccess, setNotionSaveSuccess] = useState(false);

  // Google Docs settings state
  const [googleDocsEnabled, setGoogleDocsEnabled] = useState(false);
  const [googleDocsFolderId, setGoogleDocsFolderId] = useState('');
  const [googleDocsSaving, setGoogleDocsSaving] = useState(false);
  const [googleDocsSaveSuccess, setGoogleDocsSaveSuccess] = useState(false);

  // Slack settings state
  const [slackEnabled, setSlackEnabled] = useState(false);
  const [slackBotToken, setSlackBotToken] = useState('');
  const [showSlackToken, setShowSlackToken] = useState(false);
  const [slackSaving, setSlackSaving] = useState(false);
  const [slackSaveSuccess, setSlackSaveSuccess] = useState(false);
  const [slackMonitoredIds, setSlackMonitoredIds] = useState<Set<string>>(new Set());
  const [slackChannels, setSlackChannels] = useState<SlackChannelInfo[]>([]);
  const [slackChannelsLoading, setSlackChannelsLoading] = useState(false);
  const [slackChannelsError, setSlackChannelsError] = useState<string | null>(null);
  const [slackChannelSearch, setSlackChannelSearch] = useState('');
  const [slackJoiningId, setSlackJoiningId] = useState<string | null>(null);
  const [slackLeavingId, setSlackLeavingId] = useState<string | null>(null);

  // Teams settings state
  const [teamsEnabled, setTeamsEnabled] = useState(false);
  const [teamsAccessToken, setTeamsAccessToken] = useState('');
  const [showTeamsToken, setShowTeamsToken] = useState(false);
  const [teamsSaving, setTeamsSaving] = useState(false);
  const [teamsSaveSuccess, setTeamsSaveSuccess] = useState(false);

  // Settings section navigation
  type SettingsSection = 'profile' | 'security' | 'bitbucket' | 'github' | 'gitlab' | 'jira' | 'linear' | 'monday' | 'trello' | 'confluence' | 'notion' | 'google-docs' | 'slack' | 'teams' | 'notifications' | 'advanced' | 'data' | 'ai-settings';
  const [activeSection, setActiveSection] = useState<SettingsSection>('profile');

  // Notification settings state
  const [aiSettingsDraft, setAiSettingsDraft] = useState<Partial<AiSettings>>({ provider: 'openai' });
  const [aiSettingsSaved, setAiSettingsSaved] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiSaveSuccess, setAiSaveSuccess] = useState(false);

  useEffect(() => {
    getAiSettings().then(s => { if (s) { setAiSettingsDraft(s); setAiSettingsSaved(true); } });
  }, []);

  const handleSaveAiSettings = async () => {
    if (!aiSettingsDraft.provider || !aiSettingsDraft.apiKey) return;
    setAiSaving(true);
    await saveAiSettings({ provider: aiSettingsDraft.provider, apiKey: aiSettingsDraft.apiKey, model: aiSettingsDraft.model, customEndpoint: aiSettingsDraft.customEndpoint });
    setAiSettingsSaved(true);
    setAiSaving(false);
    setAiSaveSuccess(true);
    setTimeout(() => setAiSaveSuccess(false), 2000);
  };

  const handleClearAiSettings = async () => {
    await clearAiSettings();
    setAiSettingsDraft({ provider: 'openai' });
    setAiSettingsSaved(false);
  };

  const [notifSettings, setNotifSettings] = useState<NotificationSettings>(() => getNotificationSettings());
  const [notifPermission, setNotifPermission] = useState<NotificationPermission | 'unsupported'>(() => getPermissionState());

  // Trello settings state
  const [trelloEnabled, setTrelloEnabled] = useState(false);
  const [trelloApiKey, setTrelloApiKey] = useState('');
  const [trelloToken, setTrelloToken] = useState('');
  const [showTrelloKey, setShowTrelloKey] = useState(false);
  const [trelloValidating, setTrelloValidating] = useState(false);
  const [trelloValidation, setTrelloValidation] = useState<{ valid: boolean; error?: string; user?: any } | null>(null);
  const [trelloSaving, setTrelloSaving] = useState(false);
  const [trelloSaveSuccess, setTrelloSaveSuccess] = useState(false);

  // Load existing settings on mount
  useEffect(() => {
    loadSettings();
    loadPlatformSettings();
    loadStatusConfig();
    setGitAliases(getStoredGitAliases());
  }, []);

  const loadStatusConfig = async () => {
    const cfg = await getStatusConfig();
    setStatusConfig(cfg);
  };

  const handleStatusAddValue = (category: StatusCategory) => {
    if (!statusNewValue.trim() || !statusConfig) return;
    const updated = { ...statusConfig, categories: { ...statusConfig.categories, [category]: [...statusConfig.categories[category], statusNewValue.trim().toLowerCase()] } };
    setStatusConfig(updated);
    saveStatusConfig(updated);
    invalidateStatusCache();
    setStatusNewValue('');
  };

  const handleStatusRemoveValue = (category: StatusCategory, index: number) => {
    if (!statusConfig) return;
    const updated = { ...statusConfig, categories: { ...statusConfig.categories, [category]: statusConfig.categories[category].filter((_, i) => i !== index) } };
    setStatusConfig(updated);
    saveStatusConfig(updated);
    invalidateStatusCache();
  };

  const handleStatusReset = async () => {
    await resetStatusConfig();
    invalidateStatusCache();
    await loadStatusConfig();
  };

  const handleStatusDragStart = (category: StatusCategory, index: number, value: string) => {
    setDragSource({ category, index, value });
  };

  const handleStatusDragOver = (e: React.DragEvent, category: StatusCategory) => {
    e.preventDefault();
    setDragOverCategory(category);
  };

  const handleStatusDragLeave = () => {
    setDragOverCategory(null);
  };

  const handleStatusDrop = (targetCategory: StatusCategory) => {
    if (!dragSource || !statusConfig || dragSource.category === targetCategory) {
      setDragSource(null);
      setDragOverCategory(null);
      return;
    }
    // Remove from source, add to target
    const newCategories = { ...statusConfig.categories };
    newCategories[dragSource.category] = newCategories[dragSource.category].filter((_, i) => i !== dragSource.index);
    newCategories[targetCategory] = [...newCategories[targetCategory], dragSource.value];
    const updated = { ...statusConfig, categories: newCategories };
    setStatusConfig(updated);
    saveStatusConfig(updated);
    invalidateStatusCache();
    setDragSource(null);
    setDragOverCategory(null);
  };

  const handleStatusDragEnd = () => {
    setDragSource(null);
    setDragOverCategory(null);
  };

  const handleStatusSync = async () => {
    if (!jiraDomain || !jiraEmail || !jiraApiToken) {
      setStatusSyncResult('Jira credentials required to sync statuses');
      return;
    }
    setStatusSyncing(true);
    setStatusSyncResult(null);
    try {
      const { BrowserJiraService } = await import('@/services/jira-service');
      const { atlassianOAuth2 } = await import('@/services/atlassian-oauth2');
      const oauthToken = await atlassianOAuth2.getAccessToken().catch(() => null);
      const svc = new BrowserJiraService({ domain: jiraDomain, email: jiraEmail, apiToken: jiraApiToken, ...(oauthToken ? { oauthToken } : {}) });
      const result = await svc.syncStatuses();
      setStatusSyncResult(`Synced ${result.synced} statuses${result.newStatuses.length > 0 ? ` (${result.newStatuses.length} new: ${result.newStatuses.join(', ')})` : ''}`);
      await loadStatusConfig();
    } catch (err: any) {
      setStatusSyncResult(`Sync failed: ${err.message}`);
    } finally {
      setStatusSyncing(false);
    }
  };

  const loadSettings = async () => {
    try {
      let settings: JiraSettings | null = null;

      // Try IndexedDB first, fall back to localStorage
      try {
        const data = await api.getJiraSettings();
        if (data && data.domain) {
          settings = data as JiraSettings;
        }
      } catch {}
      if (!settings) {
        settings = getStoredJiraSettings();
      }

      if (settings) {
        setJiraEnabled(settings.enabled);
        setJiraDomain(settings.domain || '');
        setJiraEmail(settings.email || '');
        setJiraApiToken(settings.apiToken || '');
        setDefaultBoardId(settings.defaultBoardId);
        setDefaultProjectKey(settings.defaultProjectKey);
        setPriorityUsers(settings.priorityUsers || []);
      }
    } catch (err) {
      console.error('Failed to load Jira settings:', err);
    }
  };

  // ==================== Profile & Git Provider Handlers ====================

  const handleSaveProfileName = useCallback((name: string) => {
    setProfileName(name);
    localStorage.setItem(PROFILE_NAME_KEY, name);
    audit.auditSettingsChanged('profile', { profileName: name });
  }, []);

  const handleValidateBitbucket = useCallback(async () => {
    if (!bbEmail || !bbToken) { setBbValidation({ valid: false, error: 'Email and token are required' }); return; }
    setBbValidating(true); setBbValidation(null);
    try {
      const result = await api.validateAuth(bbEmail, bbToken);
      setBbValidation(result);
      if (result.valid && (result as any).username) {
        setBbUsername((result as any).username);
      }
    } catch (err: any) {
      setBbValidation({ valid: false, error: err.message });
    } finally { setBbValidating(false); }
  }, [bbEmail, bbToken]);

  const handleConnectBitbucket = useCallback(async () => {
    if (!bbValidation?.valid) return;
    await api.saveBitbucketSettings({ email: bbEmail, token: bbToken, username: bbUsername || undefined });
    onLogin?.(bbEmail, bbToken, bbUsername || undefined, ghUsername || undefined, ghToken || undefined);
    onSettingsChanged?.();
  }, [bbEmail, bbToken, bbUsername, bbValidation, ghUsername, ghToken, onLogin, onSettingsChanged]);

  const handleValidateGitHub = useCallback(async () => {
    if (!ghToken) { setGhValidation({ valid: false, error: 'Personal Access Token is required' }); return; }
    setGhValidating(true); setGhValidation(null);
    try {
      const result = await api.validateGitHubAuth(ghToken);
      setGhValidation(result);
      if (result.valid && (result as any).username) {
        setGhUsername((result as any).username);
      }
    } catch (err: any) {
      setGhValidation({ valid: false, error: err.message });
    } finally { setGhValidating(false); }
  }, [ghToken]);

  const handleConnectGitHub = useCallback(async () => {
    if (!ghValidation?.valid) return;
    await api.saveGitHubSettings({ token: ghToken, username: ghUsername || '' });
    onLogin?.(bbEmail || '', bbToken || '', bbUsername || undefined, ghUsername || undefined, ghToken || undefined);
    onSettingsChanged?.();
  }, [bbEmail, bbToken, bbUsername, ghUsername, ghToken, ghValidation, onLogin, onSettingsChanged]);

  const validateJiraCredentials = useCallback(async () => {
    if (!jiraDomain || !jiraEmail || !jiraApiToken) {
      setValidationResult({ valid: false, error: 'All fields are required' });
      return;
    }

    setValidating(true);
    setValidationResult(null);

    try {
      const settings: JiraCredentials = { domain: jiraDomain, email: jiraEmail, apiToken: jiraApiToken };
      const result = await jiraTokenExpiryService.validateAndDetectExpiry(settings);

      setValidationResult({
        valid: result.valid,
        error: result.error?.message,
        user: result.expiryInfo ? undefined : result.valid ? { displayName: 'Connection successful' } : undefined
      });

      // If valid, also fetch boards and projects
      if (result.valid) {
        fetchBoardsAndProjects();
      }
    } catch (err: any) {
      setValidationResult({ valid: false, error: err.message || 'Validation failed' });
    } finally {
      setValidating(false);
    }
  }, [jiraDomain, jiraEmail, jiraApiToken]);

  const fetchBoardsAndProjects = useCallback(async () => {
    if (!jiraDomain || !jiraEmail || !jiraApiToken) return;

    setLoadingBoards(true);
    setLoadingProjects(true);

    try {
      const settings: JiraCredentials = { domain: jiraDomain, email: jiraEmail, apiToken: jiraApiToken };
      
      const [boardsResult, projectsResult] = await Promise.all([
        withJiraExpiryCheck(settings, () => api.getJiraBoards(jiraDomain, jiraEmail, jiraApiToken)),
        withJiraExpiryCheck(settings, () => api.getJiraProjects(jiraDomain, jiraEmail, jiraApiToken)),
      ]);

      if (boardsResult.error) {
        console.error('Failed to fetch boards:', boardsResult.error);
      } else if (boardsResult.result) {
        const boardsData = boardsResult.result;
        if (Array.isArray(boardsData)) {
          setBoards(boardsData);
        } else if (boardsData && typeof boardsData === 'object' && 'boards' in boardsData) {
          setBoards((boardsData as any).boards);
        }
      }

      if (projectsResult.error) {
        console.error('Failed to fetch projects:', projectsResult.error);
      } else if (projectsResult.result) {
        const projectsData = projectsResult.result;
        if (Array.isArray(projectsData)) {
          setProjects(projectsData);
        } else if (projectsData && typeof projectsData === 'object' && 'projects' in projectsData) {
          setProjects((projectsData as any).projects);
        }
      }
    } catch (err) {
      console.error('Failed to fetch boards/projects:', err);
    } finally {
      setLoadingBoards(false);
      setLoadingProjects(false);
    }
  }, [jiraDomain, jiraEmail, jiraApiToken]);

  const saveSettings = useCallback(async () => {
    setSaving(true);
    setSaveSuccess(false);

    const settings: JiraSettings = {
      enabled: jiraEnabled,
      domain: jiraDomain,
      email: jiraEmail,
      apiToken: jiraApiToken,
      defaultBoardId,
      defaultProjectKey,
      priorityUsers
};

    try {
      await api.saveJiraSettings(settings);
      storeJiraSettings(settings);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      audit.auditIntegrationConfigured('jira', { domain: jiraDomain, enabled: jiraEnabled });
      onSettingsChanged?.();
    } catch (err) {
      console.error('Failed to save Jira settings:', err);
    } finally {
      setSaving(false);
    }
  }, [jiraEnabled, jiraDomain, jiraEmail, jiraApiToken, defaultBoardId, defaultProjectKey, priorityUsers]);

  const clearSettings = useCallback(async () => {
    try {
      await api.clearJiraSettings();
      clearStoredJiraSettings();
      setJiraEnabled(false);
      setJiraDomain('');
      setJiraEmail('');
      setJiraApiToken('');
      setDefaultBoardId(undefined);
      setDefaultProjectKey(undefined);
      setPriorityUsers([]);
      setAvailableUsers([]);
      setValidationResult(null);
      setBoards([]);
      setProjects([]);
    } catch (err) {
      console.error('Failed to clear Jira settings:', err);
    }
  }, []);

  // ==================== Platform Settings (Linear, Monday, Trello) ====================

  const loadPlatformSettings = async () => {
    try {
      // Load Bitbucket and GitHub credentials from IndexedDB
      const [bbData, ghData] = await Promise.all([
        api.getBitbucketSettings().catch(() => null),
        api.getGitHubSettings().catch(() => null),
      ]);
      if (bbData?.email) {
        setBbEmail(bbData.email);
        setBbToken(bbData.token || '');
        setBbUsername(bbData.username || '');
        setBbValidation({ valid: true });
      }
      if (ghData?.token) {
        setGhToken(ghData.token);
        setGhUsername(ghData.username || '');
        setGhValidation({ valid: true });
      }

      const [linearData, mondayData, trelloData] = await Promise.all([
        api.getLinearSettings(),
        api.getMondaySettings(),
        api.getTrelloSettings(),
      ]);
      if (linearData) {
        setLinearEnabled(linearData.enabled);
        setLinearApiKey(linearData.apiKey || '');
      }
      if (mondayData) {
        setMondayEnabled(mondayData.enabled);
        setMondayApiKey(mondayData.apiKey || '');
      }
      if (trelloData) {
        setTrelloEnabled(trelloData.enabled);
        setTrelloApiKey(trelloData.apiKey || '');
        setTrelloToken(trelloData.token || '');
      }

      // Load GitLab, Notion, Google Docs settings
      const [glData, notionData, gDocsData] = await Promise.all([
        api.getGitLabSettings().catch(() => null),
        api.getNotionSettings().catch(() => null),
        api.getGoogleDocsSettings().catch(() => null),
      ]);
      if (glData?.token) {
        setGlInstanceUrl(glData.instanceUrl || 'https://gitlab.com');
        setGlToken(glData.token);
        setGlUsername(glData.username || '');
        setGlValidation({ valid: true, username: glData.username });
      }
      if (notionData) {
        setNotionEnabled(notionData.enabled);
        setNotionApiKey(notionData.apiKey || '');
      }
      if (gDocsData) {
        setGoogleDocsEnabled(gDocsData.enabled);
        setGoogleDocsFolderId(gDocsData.folderId || '');
      }
    } catch (err) {
      console.error('Failed to load platform settings:', err);
    }
  };

  const handleValidateLinear = useCallback(async () => {
    if (!linearApiKey) { setLinearValidation({ valid: false, error: 'API key is required' }); return; }
    setLinearValidating(true); setLinearValidation(null);
    try {
      const result = await validateLinearCredentials({ apiKey: linearApiKey });
      setLinearValidation(result);
    } catch (err: any) {
      setLinearValidation({ valid: false, error: err.message });
    } finally { setLinearValidating(false); }
  }, [linearApiKey]);

  const handleSaveLinear = useCallback(async () => {
    setLinearSaving(true); setLinearSaveSuccess(false);
    try {
      await api.saveLinearSettings({ enabled: linearEnabled, apiKey: linearApiKey });
      setLinearSaveSuccess(true);
      setTimeout(() => setLinearSaveSuccess(false), 3000);
      audit.auditIntegrationConfigured('linear', { enabled: linearEnabled });
      onSettingsChanged?.();
    } catch (err) { console.error('Failed to save Linear settings:', err); }
    finally { setLinearSaving(false); }
  }, [linearEnabled, linearApiKey]);

  const handleValidateMonday = useCallback(async () => {
    if (!mondayApiKey) { setMondayValidation({ valid: false, error: 'API key is required' }); return; }
    setMondayValidating(true); setMondayValidation(null);
    try {
      const result = await validateMondayCredentials({ apiKey: mondayApiKey });
      setMondayValidation(result);
    } catch (err: any) {
      setMondayValidation({ valid: false, error: err.message });
    } finally { setMondayValidating(false); }
  }, [mondayApiKey]);

  const handleSaveMonday = useCallback(async () => {
    setMondaySaving(true); setMondaySaveSuccess(false);
    try {
      await api.saveMondaySettings({ enabled: mondayEnabled, apiKey: mondayApiKey });
      setMondaySaveSuccess(true);
      setTimeout(() => setMondaySaveSuccess(false), 3000);
      onSettingsChanged?.();
    } catch (err) { console.error('Failed to save Monday settings:', err); }
    finally { setMondaySaving(false); }
  }, [mondayEnabled, mondayApiKey]);

  const handleValidateTrello = useCallback(async () => {
    if (!trelloApiKey || !trelloToken) { setTrelloValidation({ valid: false, error: 'API key and token are required' }); return; }
    setTrelloValidating(true); setTrelloValidation(null);
    try {
      const result = await validateTrelloCredentials({ apiKey: trelloApiKey, token: trelloToken });
      setTrelloValidation(result);
    } catch (err: any) {
      setTrelloValidation({ valid: false, error: err.message });
    } finally { setTrelloValidating(false); }
  }, [trelloApiKey, trelloToken]);

  const handleSaveTrello = useCallback(async () => {
    setTrelloSaving(true); setTrelloSaveSuccess(false);
    try {
      await api.saveTrelloSettings({ enabled: trelloEnabled, apiKey: trelloApiKey, token: trelloToken });
      setTrelloSaveSuccess(true);
      setTimeout(() => setTrelloSaveSuccess(false), 3000);
      audit.auditIntegrationConfigured('trello', { enabled: trelloEnabled });
      onSettingsChanged?.();
    } catch (err) { console.error('Failed to save Trello settings:', err); }
    finally { setTrelloSaving(false); }
  }, [trelloEnabled, trelloApiKey, trelloToken]);

  // ==================== GitLab handlers ====================

  const handleValidateGitLab = useCallback(async () => {
    if (!glToken) { setGlValidation({ valid: false, error: 'Token is required' }); return; }
    setGlValidating(true); setGlValidation(null);
    try {
      const result = await api.validateGitLabAuth(glToken, glInstanceUrl);
      if (result.valid && result.username) setGlUsername(result.username);
      setGlValidation(result);
    } catch (err: any) {
      setGlValidation({ valid: false, error: err.message });
    } finally { setGlValidating(false); }
  }, [glToken, glInstanceUrl]);

  const handleConnectGitLab = useCallback(async () => {
    if (!glValidation?.valid) return;
    await api.saveGitLabSettings({ instanceUrl: glInstanceUrl, token: glToken, username: glUsername || undefined });
    onSettingsChanged?.();
  }, [glToken, glInstanceUrl, glUsername, glValidation]);

  // ==================== Notion handlers ====================

  const handleValidateNotion = useCallback(async () => {
    if (!notionApiKey) { setNotionValidation({ valid: false, error: 'Integration token is required' }); return; }
    setNotionValidating(true); setNotionValidation(null);
    try {
      const response = await fetch('https://api.notion.com/v1/users/me', {
        headers: { 'Authorization': `Bearer ${notionApiKey}`, 'Notion-Version': '2022-06-28' },
      });
      if (!response.ok) {
        setNotionValidation({ valid: false, error: `HTTP ${response.status}` });
      } else {
        const data = await response.json();
        setNotionValidation({ valid: true, workspaceName: data.name || data.bot?.workspace_name || 'Connected' });
      }
    } catch (err: any) {
      setNotionValidation({ valid: false, error: err.message });
    } finally { setNotionValidating(false); }
  }, [notionApiKey]);

  const handleSaveNotion = useCallback(async () => {
    setNotionSaving(true); setNotionSaveSuccess(false);
    try {
      await api.saveNotionSettings({ enabled: notionEnabled, apiKey: notionApiKey, workspaceName: notionValidation?.workspaceName });
      setNotionSaveSuccess(true);
      setTimeout(() => setNotionSaveSuccess(false), 3000);
      onSettingsChanged?.();
    } catch (err) { console.error('Failed to save Notion settings:', err); }
    finally { setNotionSaving(false); }
  }, [notionEnabled, notionApiKey, notionValidation]);

  // ==================== Google Docs handlers ====================

  const handleSaveGoogleDocs = useCallback(async () => {
    setGoogleDocsSaving(true); setGoogleDocsSaveSuccess(false);
    try {
      await api.saveGoogleDocsSettings({ enabled: googleDocsEnabled, folderId: googleDocsFolderId || undefined });
      setGoogleDocsSaveSuccess(true);
      setTimeout(() => setGoogleDocsSaveSuccess(false), 3000);
      onSettingsChanged?.();
    } catch (err) { console.error('Failed to save Google Docs settings:', err); }
    finally { setGoogleDocsSaving(false); }
  }, [googleDocsEnabled, googleDocsFolderId]);

  // ==================== Slack handlers ====================

  useEffect(() => {
    getSlackSettings().then(s => {
      if (s) {
        setSlackEnabled(s.enabled);
        setSlackBotToken(s.accessToken || '');
        setSlackMonitoredIds(new Set(s.monitoredChannelIds));
      }
    });
  }, []);

  const loadSlackChannels = useCallback(async () => {
    if (!slackBotToken.trim()) return;
    setSlackChannelsLoading(true);
    setSlackChannelsError(null);
    try {
      const chs = await slackService.listAllChannels(slackBotToken.trim());
      setSlackChannels(chs);
    } catch (err) {
      setSlackChannelsError((err as Error).message);
    } finally {
      setSlackChannelsLoading(false);
    }
  }, [slackBotToken]);

  const handleJoinSlackChannel = useCallback(async (channelId: string) => {
    if (!slackBotToken.trim()) return;
    setSlackJoiningId(channelId);
    try {
      await slackService.joinChannel(slackBotToken.trim(), channelId);
      setSlackChannels(prev => prev.map(ch => ch.id === channelId ? { ...ch, isMember: true } : ch));
      setSlackMonitoredIds(prev => new Set([...prev, channelId]));
    } catch (err) {
      console.error('Failed to join Slack channel:', err);
    } finally {
      setSlackJoiningId(null);
    }
  }, [slackBotToken]);

  const handleLeaveSlackChannel = useCallback(async (channelId: string) => {
    if (!slackBotToken.trim()) return;
    setSlackLeavingId(channelId);
    try {
      await slackService.leaveChannel(slackBotToken.trim(), channelId);
      setSlackChannels(prev => prev.map(ch => ch.id === channelId ? { ...ch, isMember: false } : ch));
      setSlackMonitoredIds(prev => { const n = new Set(prev); n.delete(channelId); return n; });
    } catch (err) {
      console.error('Failed to leave Slack channel:', err);
    } finally {
      setSlackLeavingId(null);
    }
  }, [slackBotToken]);

  const handleSaveSlack = useCallback(async () => {
    setSlackSaving(true); setSlackSaveSuccess(false);
    try {
      const existing = await getSlackSettings();
      await saveSlackSettings({
        teamId: existing?.teamId ?? '',
        teamName: existing?.teamName ?? '',
        botUserId: existing?.botUserId ?? '',
        monitoredChannelIds: [...slackMonitoredIds],
        lastPolledAt: existing?.lastPolledAt,
        enabled: slackEnabled,
        accessToken: slackBotToken.trim(),
      });
      setSlackSaveSuccess(true);
      setTimeout(() => setSlackSaveSuccess(false), 3000);
      audit.auditIntegrationConfigured('slack', { enabled: slackEnabled });
      onSettingsChanged?.();
    } catch (err) { console.error('Failed to save Slack settings:', err); }
    finally { setSlackSaving(false); }
  }, [slackEnabled, slackBotToken, slackMonitoredIds, onSettingsChanged]);

  // ==================== Teams handlers ====================

  useEffect(() => {
    getTeamsSettings().then(s => {
      if (s) { setTeamsEnabled(s.enabled); setTeamsAccessToken(s.accessToken || ''); }
    });
  }, []);

  const handleSaveTeams = useCallback(async () => {
    setTeamsSaving(true); setTeamsSaveSuccess(false);
    try {
      const existing = await getTeamsSettings();
      await saveTeamsSettings({
        tenantId: existing?.tenantId ?? '',
        tenantName: existing?.tenantName ?? '',
        monitoredTeamIds: existing?.monitoredTeamIds ?? [],
        lastPolledAt: existing?.lastPolledAt,
        enabled: teamsEnabled,
        accessToken: teamsAccessToken.trim(),
      });
      setTeamsSaveSuccess(true);
      setTimeout(() => setTeamsSaveSuccess(false), 3000);
      audit.auditIntegrationConfigured('teams', { enabled: teamsEnabled });
      onSettingsChanged?.();
    } catch (err) { console.error('Failed to save Teams settings:', err); }
    finally { setTeamsSaving(false); }
  }, [teamsEnabled, teamsAccessToken, onSettingsChanged]);

  // ==================== Platform OAuth2 effects (GitHub, Bitbucket, Linear, Monday) ====================

  useEffect(() => {
    githubOAuth2.getState().then(setGhOAuthState);
    const unsub = githubOAuth2.onStateChange(setGhOAuthState);
    return unsub;
  }, []);

  useEffect(() => {
    bitbucketOAuth2.getState().then(setBbOAuthState);
    const unsub = bitbucketOAuth2.onStateChange(setBbOAuthState);
    return unsub;
  }, []);

  useEffect(() => {
    linearOAuth2.getState().then(setLinearOAuthState);
    const unsub = linearOAuth2.onStateChange(setLinearOAuthState);
    return unsub;
  }, []);

  useEffect(() => {
    mondayOAuth2.getState().then(setMondayOAuthState);
    const unsub = mondayOAuth2.onStateChange(setMondayOAuthState);
    return unsub;
  }, []);

  // ==================== Confluence OAuth2 ====================

  useEffect(() => {
    atlassianOAuth2.getState().then(setConfluenceOAuthState);
    const unsub = atlassianOAuth2.onStateChange(setConfluenceOAuthState);
    // Load saved credentials from IndexedDB
    getConfluenceSettings().then((s) => {
      if (s?.clientId) setConfluenceClientId(s.clientId);
      if (s?.clientSecret) setConfluenceClientSecret(s.clientSecret);
      if (s?.enabled !== undefined) setConfluenceEnabled(s.enabled);
    });
    return unsub;
  }, []);

  const handleConfluenceConnect = useCallback(async () => {
    if (!confluenceClientId.trim()) {
      setConfluenceError('OAuth2 Client ID is required. Register an app at developer.atlassian.com');
      return;
    }
    setConfluenceConnecting(true);
    setConfluenceError(null);
    if (!confluenceClientSecret.trim()) {
      setConfluenceError('OAuth2 Secret is required. Copy it from your app settings at developer.atlassian.com');
      setConfluenceConnecting(false);
      return;
    }
    try {
      // Persist credentials so they survive page reloads
      await saveConfluenceSettings({
        enabled: true,
        cloudId: '',
        cloudName: '',
        clientId: confluenceClientId.trim(),
        clientSecret: confluenceClientSecret.trim()
});
      atlassianOAuth2.configure(confluenceClientId.trim(), confluenceClientSecret.trim());
      const token = await atlassianOAuth2.initiateFlow();
      // Update saved settings with the resolved cloud info
      await saveConfluenceSettings({
        enabled: true,
        cloudId: token.cloudId ?? '',
        cloudName: token.cloudName ?? '',
        clientId: confluenceClientId.trim(),
        clientSecret: confluenceClientSecret.trim()
});
      setConfluenceOAuthState(await atlassianOAuth2.getState());
      setConfluenceEnabled(true);
      audit.auditIntegrationConfigured('confluence', { cloudName: token.cloudName });
      onSettingsChanged?.();
    } catch (err: any) {
      setConfluenceError(err.message ?? 'Connection failed');
    } finally {
      setConfluenceConnecting(false);
    }
  }, [confluenceClientId, confluenceClientSecret]);

  const handleConfluenceDisconnect = useCallback(async () => {
    await atlassianOAuth2.revokeToken();
    setConfluenceOAuthState(await atlassianOAuth2.getState());
    setConfluenceEnabled(false);
    // Save disabled state
    getConfluenceSettings().then(s => {
      saveConfluenceSettings({
        ...s,
        enabled: false,
        cloudId: s?.cloudId || '',
        cloudName: s?.cloudName || '',
        clientId: s?.clientId || '',
        clientSecret: s?.clientSecret || ''
      });
    });
    audit.auditIntegrationConfigured('confluence', { action: 'disconnected' });
    onSettingsChanged?.();
  }, []);

  // ==================== End Confluence OAuth2 ====================

  // ==================== Platform OAuth2 handlers ====================

  const handleGitHubOAuthConnect = useCallback(async () => {
    if (!ghOAuthClientId.trim() || !ghOAuthClientSecret.trim()) {
      setGhOAuthError('Client ID and Client Secret are required. Register a GitHub OAuth App at github.com/settings/applications/new');
      return;
    }
    setGhOAuthConnecting(true);
    setGhOAuthError(null);
    try {
      githubOAuth2.configure(ghOAuthClientId.trim(), ghOAuthClientSecret.trim());
      await githubOAuth2.initiateFlow();
      setGhOAuthState(await githubOAuth2.getState());
      audit.auditIntegrationConfigured('github', { method: 'oauth2' });
      onSettingsChanged?.();
    } catch (err: any) {
      setGhOAuthError(err.message ?? 'Connection failed');
    } finally {
      setGhOAuthConnecting(false);
    }
  }, [ghOAuthClientId, ghOAuthClientSecret, onSettingsChanged]);

  const handleGitHubOAuthDisconnect = useCallback(async () => {
    await githubOAuth2.revokeToken();
    setGhOAuthState(await githubOAuth2.getState());
    audit.auditIntegrationConfigured('github', { action: 'oauth2-disconnected' });
    onSettingsChanged?.();
  }, [onSettingsChanged]);

  const handleBitbucketOAuthConnect = useCallback(async () => {
    if (!bbOAuthClientId.trim() || !bbOAuthClientSecret.trim()) {
      setBbOAuthError('Client ID and Client Secret are required. Register a Bitbucket OAuth Consumer in your workspace settings.');
      return;
    }
    setBbOAuthConnecting(true);
    setBbOAuthError(null);
    try {
      bitbucketOAuth2.configure(bbOAuthClientId.trim(), bbOAuthClientSecret.trim());
      await bitbucketOAuth2.initiateFlow();
      setBbOAuthState(await bitbucketOAuth2.getState());
      audit.auditIntegrationConfigured('bitbucket', { method: 'oauth2' });
      onSettingsChanged?.();
    } catch (err: any) {
      setBbOAuthError(err.message ?? 'Connection failed');
    } finally {
      setBbOAuthConnecting(false);
    }
  }, [bbOAuthClientId, bbOAuthClientSecret, onSettingsChanged]);

  const handleBitbucketOAuthDisconnect = useCallback(async () => {
    await bitbucketOAuth2.revokeToken();
    setBbOAuthState(await bitbucketOAuth2.getState());
    audit.auditIntegrationConfigured('bitbucket', { action: 'oauth2-disconnected' });
    onSettingsChanged?.();
  }, [onSettingsChanged]);

  const handleLinearOAuthConnect = useCallback(async () => {
    if (!linearOAuthClientId.trim() || !linearOAuthClientSecret.trim()) {
      setLinearOAuthError('Client ID and Client Secret are required. Register a Linear OAuth App at linear.app/settings/api/applications/new');
      return;
    }
    setLinearOAuthConnecting(true);
    setLinearOAuthError(null);
    try {
      linearOAuth2.configure(linearOAuthClientId.trim(), linearOAuthClientSecret.trim());
      await linearOAuth2.initiateFlow();
      setLinearOAuthState(await linearOAuth2.getState());
      audit.auditIntegrationConfigured('linear', { method: 'oauth2' });
      onSettingsChanged?.();
    } catch (err: any) {
      setLinearOAuthError(err.message ?? 'Connection failed');
    } finally {
      setLinearOAuthConnecting(false);
    }
  }, [linearOAuthClientId, linearOAuthClientSecret, onSettingsChanged]);

  const handleLinearOAuthDisconnect = useCallback(async () => {
    await linearOAuth2.revokeToken();
    setLinearOAuthState(await linearOAuth2.getState());
    audit.auditIntegrationConfigured('linear', { action: 'oauth2-disconnected' });
    onSettingsChanged?.();
  }, [onSettingsChanged]);

  const handleMondayOAuthConnect = useCallback(async () => {
    if (!mondayOAuthClientId.trim() || !mondayOAuthClientSecret.trim()) {
      setMondayOAuthError('Client ID and Client Secret are required. Register a Monday.com OAuth App in your developer account.');
      return;
    }
    setMondayOAuthConnecting(true);
    setMondayOAuthError(null);
    try {
      mondayOAuth2.configure(mondayOAuthClientId.trim(), mondayOAuthClientSecret.trim());
      await mondayOAuth2.initiateFlow();
      setMondayOAuthState(await mondayOAuth2.getState());
      audit.auditIntegrationConfigured('monday', { method: 'oauth2' });
      onSettingsChanged?.();
    } catch (err: any) {
      setMondayOAuthError(err.message ?? 'Connection failed');
    } finally {
      setMondayOAuthConnecting(false);
    }
  }, [mondayOAuthClientId, mondayOAuthClientSecret, onSettingsChanged]);

  const handleMondayOAuthDisconnect = useCallback(async () => {
    await mondayOAuth2.revokeToken();
    setMondayOAuthState(await mondayOAuth2.getState());
    audit.auditIntegrationConfigured('monday', { action: 'oauth2-disconnected' });
    onSettingsChanged?.();
  }, [onSettingsChanged]);

  // ==================== End Platform OAuth2 handlers ==================== 

  const fetchProjectUsers = useCallback(async (projectKey: string) => {
    if (!jiraDomain || !jiraEmail || !jiraApiToken || !projectKey) return;
    setLoadingUsers(true);
    try {
      let data: any;
      data = await api.getJiraProjectUsers(jiraDomain, jiraEmail, jiraApiToken, projectKey);
      if (data?.users) {
        setAvailableUsers(data.users);
      }
    } catch (err) {
      console.error('Failed to fetch project users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, [jiraDomain, jiraEmail, jiraApiToken]);

  // Fetch users when default project changes
  useEffect(() => {
    if (defaultProjectKey && validationResult?.valid) {
      fetchProjectUsers(defaultProjectKey);
    }
  }, [defaultProjectKey, validationResult?.valid, fetchProjectUsers]);

  // Keyboard shortcut: ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Helper map for service config (poc/beta status)
  const serviceConfig: Record<string, { poc: boolean; beta: boolean }> = {
    github:      { poc: false, beta: false },
    gitlab:      { poc: true,  beta: false },
    linear:      { poc: false, beta: false },
    monday:      { poc: false, beta: true },
    trello:      { poc: false, beta: true },
    notion:      { poc: true,  beta: false },
    'google-docs': { poc: true,  beta: false },
    slack:       { poc: false,  beta: true },
    teams:       { poc: true,  beta: false },
  };

  return (
    <div className={inline ? 'flex flex-col min-h-full' : 'fixed inset-0 z-[100] flex flex-col bg-gray-50 dark:bg-slate-900'}>
      {/* Header */}
      {!inline && (
        <div className="flex-shrink-0 h-14 border-b border-gray-200 dark:border-slate-700/50 bg-white dark:bg-slate-900 flex items-center justify-between px-5 z-10">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
              <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4 text-gray-500 dark:text-slate-400" />
            </button>
            <FontAwesomeIcon icon={faGear} className="h-4 w-4 text-blue-500" />
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Settings</h1>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-gray-500 dark:text-slate-400" />
          </button>
        </div>
      )}

      {/* Body: sidebar + content */}
      <div className={`flex flex-1 min-h-0 ${!inline ? '' : 'min-h-screen'}`}>
        {/* Sidebar nav */}
        <aside className="w-52 flex-shrink-0 border-r border-gray-200 dark:border-slate-700/50 bg-white dark:bg-slate-950/70 overflow-y-auto flex flex-col">
          <nav className="flex-1 py-4">
            <div className="mb-5">
              <p className="px-4 mb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-slate-500">General</p>
              {([
                { id: 'profile' as const, label: 'Profile', dot: false, icon: <FontAwesomeIcon icon={faCircleUser} className="h-4 w-4 flex-shrink-0 text-slate-400" /> },
                { id: 'security' as const, label: 'Security', dot: false, icon: <FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4 flex-shrink-0 text-emerald-500" /> },
                              ]).map(item => (
                <button key={item.id} onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm border-r-2 transition-colors ${activeSection === item.id ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500 font-medium' : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 border-transparent'}`}>
                  {item.icon}<span className="flex-1 text-left ml-2.5">{item.label}</span>
                  {item.dot && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
                </button>
              ))}
            </div>
            <div className="mb-5">
              <p className="px-4 mb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-slate-500">Integrations</p>

              {/* Atlassian group */}
              <div className="mb-0.5">
                <div className="flex items-center gap-2.5 px-4 py-2 text-xs font-semibold text-gray-500 dark:text-slate-400 border-r-2 border-transparent select-none">
                  <FontAwesomeIcon icon={faAtlassian} className="h-4 w-4 flex-shrink-0 text-[#2684FF]" />
                  <span className="flex-1 ml-2.5 uppercase tracking-wide text-[10px]">Atlassian</span>
                  {(jiraEnabled || (confluenceEnabled && confluenceOAuthState?.isAuthenticated) || bbOAuthState?.isAuthenticated || bbValidation?.valid) && (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                  )}
                </div>
                {/* Jira sub-item */}
                <button onClick={() => setActiveSection('jira')}
                  className={`w-full flex items-center gap-2 pl-8 pr-4 py-2 text-sm border-r-2 transition-colors ${activeSection === 'jira' ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500 font-medium' : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 border-transparent'}`}>
                  <FontAwesomeIcon icon={faJira} className="h-3.5 w-3.5 flex-shrink-0 text-[#2684FF]" />
                  <span className="flex-1 text-left">Jira</span>
                  {jiraEnabled && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
                </button>
                {/* Confluence sub-item */}
                <button onClick={() => setActiveSection('confluence')}
                  className={`w-full flex items-center gap-2 pl-8 pr-4 py-2 text-sm border-r-2 transition-colors ${activeSection === 'confluence' ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500 font-medium' : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 border-transparent'}`}>
                  <FontAwesomeIcon icon={faConfluence} className="h-3.5 w-3.5 flex-shrink-0 text-blue-400" />
                  <span className="flex-1 text-left">Confluence</span>
                  {confluenceEnabled && confluenceOAuthState?.isAuthenticated && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
                </button>
                {/* Bitbucket sub-item */}
                <button onClick={() => setActiveSection('bitbucket')}
                  className={`w-full flex items-center gap-2 pl-8 pr-4 py-2 text-sm border-r-2 transition-colors ${activeSection === 'bitbucket' ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500 font-medium' : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 border-transparent'}`}>
                  <FontAwesomeIcon icon={faBitbucket} className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                  <span className="flex-1 text-left">Bitbucket</span>
                  {(bbOAuthState?.isAuthenticated || bbValidation?.valid) && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
                </button>
              </div>

              {([
                { id: 'github' as const, label: 'GitHub', dot: ghValidation?.valid, poc: false, beta: false, icon: <FontAwesomeIcon icon={faGithub} className="h-4 w-4 flex-shrink-0 text-gray-700 dark:text-white" /> },
                { id: 'gitlab' as const, label: 'GitLab', dot: glValidation?.valid, poc: true, beta: false, icon: <FontAwesomeIcon icon={faGitlab} className="h-4 w-4 flex-shrink-0 text-orange-500" /> },
                { id: 'linear' as const, label: 'Linear', dot: linearEnabled, poc: false, beta: false, icon: <><img src="/brands/linear/dark.svg" className="h-4 w-4 flex-shrink-0 dark:hidden" alt="" /><img src="/brands/linear/light.svg" className="h-4 w-4 flex-shrink-0 hidden dark:block" alt="" /></> },
                { id: 'monday' as const, label: 'Monday.com', dot: mondayEnabled, poc: false, beta: true, icon: <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center"><img src="/brands/monday/dark.svg" className="w-4 h-auto" alt="" /></span> },
                { id: 'trello' as const, label: 'Trello', dot: trelloEnabled, poc: false, beta: true, icon: <FontAwesomeIcon icon={faTrello} className="h-4 w-4 flex-shrink-0 text-sky-400" /> },
                { id: 'notion' as const, label: 'Notion', dot: notionEnabled, poc: true, beta: false, icon: <FontAwesomeIcon icon={faNotion} className="h-4 w-4 flex-shrink-0 text-gray-700 dark:text-gray-200" /> },
                { id: 'google-docs' as const, label: 'Google Docs', dot: googleDocsEnabled, poc: true, beta: false, icon: <FontAwesomeIcon icon={faGoogle} className="h-4 w-4 flex-shrink-0 text-blue-500" /> },
                { id: 'slack' as const, label: 'Slack', dot: slackEnabled && !!slackBotToken, poc: false, beta: true, icon: <FontAwesomeIcon icon={faSlack} className="h-4 w-4 flex-shrink-0 text-[#E01E5A]" /> },
                { id: 'teams' as const, label: 'Microsoft Teams', dot: teamsEnabled && !!teamsAccessToken, poc: true, beta: false, icon: <FontAwesomeIcon icon={faMicrosoft} className="h-4 w-4 flex-shrink-0 text-[#6264A7]" /> },
              ]).map(item => (
                <button key={item.id} onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm border-r-2 transition-colors ${activeSection === item.id ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500 font-medium' : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 border-transparent'}`}>
                  <span className="flex-shrink-0">{item.icon}</span><span className="flex-1 text-left ml-2.5">{item.label}</span>
                  <span className="flex items-center gap-1 flex-shrink-0">
                    {item.poc && <span className="text-[9px] px-1 rounded bg-amber-500/20 text-amber-500 dark:text-amber-400 border border-amber-500/30 font-semibold">POC</span>}
                    {item.beta && <span className="text-[9px] px-1 rounded bg-violet-500/20 text-violet-500 dark:text-violet-400 border border-violet-500/30 font-semibold">BETA</span>}
                    {item.dot && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                  </span>
                </button>
              ))}
            </div>
            <div>
              <p className="px-4 mb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-slate-500">System</p>
              {([
                { id: 'ai-settings' as const, label: 'AI Settings', dot: aiSettingsSaved, icon: <FontAwesomeIcon icon={faWandMagicSparkles} className="h-4 w-4 flex-shrink-0 text-violet-500" /> },
                { id: 'notifications' as const, label: 'Notifications', dot: notifSettings.enabled, icon: <FontAwesomeIcon icon={faBellSolid} className="h-4 w-4 flex-shrink-0 text-amber-500" /> },
                { id: 'advanced' as const, label: 'Advanced', dot: false, icon: <FontAwesomeIcon icon={faGears} className="h-4 w-4 flex-shrink-0 text-violet-500" /> },
                { id: 'data' as const, label: 'Data & Backup', dot: false, icon: <FontAwesomeIcon icon={faDatabase} className="h-4 w-4 flex-shrink-0 text-blue-500" /> },
              ]).map(item => (
                <button key={item.id} onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm border-r-2 transition-colors ${activeSection === item.id ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500 font-medium' : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 border-transparent'}`}>
                  {item.icon}<span className="flex-1 text-left ml-2.5">{item.label}</span>
                  {item.dot && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />}
                </button>
              ))}
            </div>
          </nav>
        </aside>

        {/* Content panel */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-slate-900/50">
          <div className="p-4 w-full">
        {activeSection === 'profile' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Profile</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-3">Name this configuration and manage git author aliases</p>
          <div className="space-y-5">
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            {/* Profile Name */}
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faUser} className="h-5 w-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Profile Name</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">A name for this configuration profile (e.g. "Work", "Personal")</p>
                </div>
              </div>
              <input
                type="text"
                value={profileName}
                onChange={(e) => handleSaveProfileName(e.target.value)}
                placeholder="My Profile"
                className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500/50 transition-all"
              />
              <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-2">
                This profile contains your Bitbucket, GitHub, Jira, Linear, Monday, and Trello sub-connections below.
              </p>
            </div>
          </div>
          </div>
        </>)}
        {activeSection === 'security' && <SecuritySection />}
        {activeSection === 'bitbucket' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Bitbucket</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Connect Bitbucket for Git repository analytics</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faBitbucket} className="h-5 w-5 text-blue-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Bitbucket</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {bbOAuthState?.isAuthenticated ? (
                      <span className="text-emerald-600 dark:text-emerald-400">OAuth2{bbOAuthState.token?.username ? ` · ${bbOAuthState.token.username}` : ''}</span>
                    ) : bbValidation?.valid ? (
                      <span className="text-emerald-600 dark:text-emerald-400">API Token{bbUsername ? ` · ${bbUsername}` : ''}</span>
                    ) : 'Not connected'}
                  </p>
                </div>
                {(bbOAuthState?.isAuthenticated || bbValidation?.valid) && (
                  <span className="px-2 py-1 bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs rounded-md font-medium">Connected</span>
                )}
              </div>

              {/* OAuth2 card */}
              <div className="border border-blue-500/20 rounded-xl overflow-hidden mb-4">
                <div className="flex items-center justify-between px-3 py-2.5 bg-blue-500/5">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faShieldHalved} className="h-3.5 w-3.5 text-blue-400" />
                    <span className="text-xs font-medium text-gray-700 dark:text-slate-300">OAuth2</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 font-medium">Recommended</span>
                  </div>
                  {bbOAuthState?.isAuthenticated && (
                    <button onClick={handleBitbucketOAuthDisconnect} className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded-lg border border-red-500/20 transition-colors">
                      <FontAwesomeIcon icon={faRightFromBracket} className="h-3 w-3" /> Disconnect
                    </button>
                  )}
                </div>
                {bbOAuthState?.isAuthenticated && bbOAuthState.token ? (
                  <div className="px-3 py-2.5 grid grid-cols-2 gap-2 text-xs">
                    <div><p className="text-gray-500 dark:text-slate-400">Account</p><p className="text-gray-900 dark:text-white font-medium">{bbOAuthState.token.displayName ?? '—'}</p></div>
                    <div><p className="text-gray-500 dark:text-slate-400">Username</p><p className="text-gray-900 dark:text-white font-medium">{bbOAuthState.token.username ?? '—'}</p></div>
                  </div>
                ) : (
                  <div className="p-3 space-y-2.5">
                    <p className="text-xs text-gray-500 dark:text-slate-400">Register a <a href="https://support.atlassian.com/bitbucket-cloud/docs/use-oauth-on-bitbucket-cloud/" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">Bitbucket OAuth Consumer</a> (Workspace → Settings → OAuth consumers). Callback: <code className="text-blue-400 bg-blue-500/10 px-1 rounded select-all text-[10px]">verigit://oauth/callback</code></p>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" value={bbOAuthClientId} onChange={(e) => setBbOAuthClientId(e.target.value)} placeholder="Client ID (Key)" className="px-2.5 py-1.5 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
                      <div className="relative">
                        <input type={showBbOAuthSecret ? 'text' : 'password'} value={bbOAuthClientSecret} onChange={(e) => setBbOAuthClientSecret(e.target.value)} placeholder="Secret" className="w-full px-2.5 py-1.5 pr-7 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
                        <button type="button" onClick={() => setShowBbOAuthSecret(!showBbOAuthSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"><FontAwesomeIcon icon={faEye} className="h-3 w-3" /></button>
                      </div>
                    </div>
                    {bbOAuthError && <p className="text-xs text-red-500 dark:text-red-400">{bbOAuthError}</p>}
                    <button onClick={handleBitbucketOAuthConnect} disabled={bbOAuthConnecting || !bbOAuthClientId.trim()} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                      {bbOAuthConnecting ? <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> : <FontAwesomeIcon icon={faLink} className="h-3 w-3" />}
                      {bbOAuthConnecting ? 'Connecting…' : 'Connect Bitbucket'}
                    </button>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700/40" />
                <span className="text-[11px] text-gray-400 dark:text-slate-500 font-medium">Or use App Password</span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700/40" />
              </div>

              <div className="space-y-3">
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                    <FontAwesomeIcon icon={faEnvelope} className="h-3 w-3" /> Email
                  </label>
                  <input
                    type="email"
                    value={bbEmail}
                    onChange={(e) => { setBbEmail(e.target.value); setBbValidation(null); }}
                    placeholder="you@company.com"
                    className="w-full px-3 py-2 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                    <FontAwesomeIcon icon={faKey} className="h-3 w-3" /> API Token
                  </label>
                  <div className="relative">
                    <input
                      type={showBbToken ? 'text' : 'password'}
                      value={bbToken}
                      onChange={(e) => { setBbToken(e.target.value); setBbValidation(null); }}
                      placeholder="ATATT3xFfGF0..."
                      className="w-full px-3 py-2 pr-10 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                    />
                    <button type="button" onClick={() => setShowBbToken(!showBbToken)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-gray-600 dark:hover:text-slate-300">
                      {showBbToken ? <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4" /> : <FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {bbValidation && !bbValidation.valid && (
                  <div className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
                    <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-600 dark:text-red-300">{bbValidation.error}</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={handleValidateBitbucket} disabled={bbValidating || !bbEmail || !bbToken}
                    className="px-3 py-2 text-xs font-medium bg-blue-600 hover:bg-blue-500 text-white rounded-lg disabled:opacity-50 transition-colors flex items-center gap-1.5">
                    {bbValidating ? <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> : <FontAwesomeIcon icon={faShieldHalved} className="h-3 w-3" />}
                    {bbValidating ? 'Validating...' : 'Test Connection'}
                  </button>
                  {bbValidation?.valid && onLogin && (
                    <button onClick={handleConnectBitbucket}
                      className="px-3 py-2 text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors flex items-center gap-1.5">
                      <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" /> Save & Connect
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>)}
        {activeSection === 'github' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">GitHub</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Connect GitHub for Git repository analytics</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5 border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-gray-800/20 dark:bg-white/10 flex items-center justify-center">
                  <FontAwesomeIcon icon={faGithub} className="h-5 w-5 text-gray-700 dark:text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">GitHub</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {ghOAuthState?.isAuthenticated ? (
                      <span className="text-emerald-600 dark:text-emerald-400">OAuth2{ghOAuthState.token?.username ? ` · ${ghOAuthState.token.username}` : ''}</span>
                    ) : ghValidation?.valid ? (
                      <span className="text-emerald-600 dark:text-emerald-400">PAT{ghUsername ? ` · ${ghUsername}` : ''}</span>
                    ) : 'Not connected'}
                  </p>
                </div>
                {(ghOAuthState?.isAuthenticated || ghValidation?.valid) && (
                  <span className="px-2 py-1 bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs rounded-md font-medium">Connected</span>
                )}
              </div>

              {/* OAuth2 card */}
              <div className="border border-gray-300 dark:border-white/10 rounded-xl overflow-hidden mb-4">
                <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-white/[0.04]">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faShieldHalved} className="h-3.5 w-3.5 text-gray-500 dark:text-slate-400" />
                    <span className="text-xs font-medium text-gray-700 dark:text-slate-300">OAuth2 App</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 font-medium">Recommended</span>
                  </div>
                  {ghOAuthState?.isAuthenticated && (
                    <button onClick={handleGitHubOAuthDisconnect} className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded-lg border border-red-500/20 transition-colors">
                      <FontAwesomeIcon icon={faRightFromBracket} className="h-3 w-3" /> Disconnect
                    </button>
                  )}
                </div>
                {ghOAuthState?.isAuthenticated && ghOAuthState.token ? (
                  <div className="px-3 py-2.5 grid grid-cols-2 gap-2 text-xs">
                    <div><p className="text-gray-500 dark:text-slate-400">Account</p><p className="text-gray-900 dark:text-white font-medium">{ghOAuthState.token.displayName ?? '—'}</p></div>
                    <div><p className="text-gray-500 dark:text-slate-400">Username</p><p className="text-gray-900 dark:text-white font-medium">{ghOAuthState.token.username ?? '—'}</p></div>
                  </div>
                ) : (
                  <div className="p-3 space-y-2.5">
                    <p className="text-xs text-gray-500 dark:text-slate-400">Register a <a href="https://github.com/settings/applications/new" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">GitHub OAuth App</a>. Callback URL: <code className="text-blue-400 bg-blue-500/10 px-1 rounded select-all text-[10px]">verigit://oauth/callback</code></p>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" value={ghOAuthClientId} onChange={(e) => setGhOAuthClientId(e.target.value)} placeholder="Client ID" className="px-2.5 py-1.5 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
                      <div className="relative">
                        <input type={showGhOAuthSecret ? 'text' : 'password'} value={ghOAuthClientSecret} onChange={(e) => setGhOAuthClientSecret(e.target.value)} placeholder="Client Secret" className="w-full px-2.5 py-1.5 pr-7 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
                        <button type="button" onClick={() => setShowGhOAuthSecret(!showGhOAuthSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"><FontAwesomeIcon icon={faEye} className="h-3 w-3" /></button>
                      </div>
                    </div>
                    {ghOAuthError && <p className="text-xs text-red-500 dark:text-red-400">{ghOAuthError}</p>}
                    <button onClick={handleGitHubOAuthConnect} disabled={ghOAuthConnecting || !ghOAuthClientId.trim()} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 dark:bg-white/10 dark:hover:bg-white/15 text-white text-xs font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                      {ghOAuthConnecting ? <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> : <FontAwesomeIcon icon={faGithub} className="h-3 w-3" />}
                      {ghOAuthConnecting ? 'Connecting…' : 'Connect GitHub'}
                    </button>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700/40" />
                <span className="text-[11px] text-gray-400 dark:text-slate-500 font-medium">Or use Personal Access Token</span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700/40" />
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide">
                      <FontAwesomeIcon icon={faKey} className="h-3 w-3" /> Personal Access Token
                    </label>
                    <a href="https://github.com/settings/tokens?type=beta" target="_blank" rel="noopener noreferrer"
                      className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      Create token <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-2.5 w-2.5" />
                    </a>
                  </div>
                  <div className="relative">
                    <input
                      type={showGhToken ? 'text' : 'password'}
                      value={ghToken}
                      onChange={(e) => { setGhToken(e.target.value); setGhValidation(null); }}
                      placeholder="ghp_xxxxxxxxxxxx..."
                      className="w-full px-3 py-2 pr-10 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                    />
                    <button type="button" onClick={() => setShowGhToken(!showGhToken)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-gray-600 dark:hover:text-slate-300">
                      {showGhToken ? <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4" /> : <FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {ghValidation && !ghValidation.valid && (
                  <div className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
                    <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-600 dark:text-red-300">{ghValidation.error}</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={handleValidateGitHub} disabled={ghValidating || !ghToken}
                    className="px-3 py-2 text-xs font-medium bg-gray-800 hover:bg-gray-700 dark:bg-white/10 dark:hover:bg-white/15 text-white rounded-lg disabled:opacity-50 transition-colors flex items-center gap-1.5">
                    {ghValidating ? <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> : <FontAwesomeIcon icon={faGithub} className="h-3 w-3" />}
                    {ghValidating ? 'Validating...' : 'Test Connection'}
                  </button>
                  {ghValidation?.valid && onLogin && (
                    <button onClick={handleConnectGitHub}
                      className="px-3 py-2 text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors flex items-center gap-1.5">
                      <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" /> Save & Connect
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>)}
        {activeSection === 'gitlab' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">GitLab</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Connect GitLab for Git repository analytics</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faGitlab} className="h-5 w-5 text-orange-500" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">GitLab</p>
                    {serviceConfig.gitlab.poc && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 dark:text-amber-400 border border-amber-500/30 font-semibold">POC</span>}
                    {serviceConfig.gitlab.beta && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-500 dark:text-violet-400 border border-violet-500/30 font-semibold">BETA</span>}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {glValidation?.valid ? (
                      <span className="text-emerald-600 dark:text-emerald-400">Connected{glUsername ? ` · ${glUsername}` : ''}</span>
                    ) : 'Not connected'}
                  </p>
                </div>
                {glValidation?.valid && (
                  <span className="px-2 py-1 bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs rounded-md font-medium">Connected</span>
                )}
              </div>

              <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">Supports GitLab.com and self-hosted instances. Create a <a href="https://docs.gitlab.com/ee/user/profile/personal_access_tokens.html" target="_blank" rel="noreferrer" className="text-orange-400 hover:underline">Personal Access Token</a> with <code className="text-orange-400 bg-orange-500/10 px-1 rounded text-[10px]">read_api</code> scope.</p>

              <div className="space-y-3">
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                    <FontAwesomeIcon icon={faGlobe} className="h-3 w-3" /> Instance URL
                  </label>
                  <input
                    type="url"
                    value={glInstanceUrl}
                    onChange={(e) => { setGlInstanceUrl(e.target.value); setGlValidation(null); }}
                    placeholder="https://gitlab.com"
                    className="w-full px-3 py-2 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500/50 transition-all"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                    <FontAwesomeIcon icon={faKey} className="h-3 w-3" /> Personal Access Token
                  </label>
                  <div className="relative">
                    <input
                      type={showGlToken ? 'text' : 'password'}
                      value={glToken}
                      onChange={(e) => { setGlToken(e.target.value); setGlValidation(null); }}
                      placeholder="glpat-..."
                      className="w-full px-3 py-2 pr-10 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500/50 transition-all"
                    />
                    <button type="button" onClick={() => setShowGlToken(!showGlToken)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-gray-600 dark:hover:text-slate-300">
                      {showGlToken ? <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4" /> : <FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {glValidation && !glValidation.valid && (
                  <div className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
                    <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-600 dark:text-red-300">{glValidation.error}</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={handleValidateGitLab} disabled={glValidating || !glToken}
                    className="px-3 py-2 text-xs font-medium bg-orange-600 hover:bg-orange-500 text-white rounded-lg disabled:opacity-50 transition-colors flex items-center gap-1.5">
                    {glValidating ? <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> : <FontAwesomeIcon icon={faShieldHalved} className="h-3 w-3" />}
                    {glValidating ? 'Validating...' : 'Test Connection'}
                  </button>
                  {glValidation?.valid && (
                    <button onClick={handleConnectGitLab}
                      className="px-3 py-2 text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors flex items-center gap-1.5">
                      <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" /> Save & Connect
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>)}

        {activeSection === 'jira' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Jira</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Connect Jira Cloud to track issues, sprints, and quality metrics</p>
          {/* Jira Integration Card */}
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            {/* Toggle Header */}
            <div className="p-5 flex items-center justify-between border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faJira} className="h-5 w-5 text-[#2684FF]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Jira Cloud</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Track issues, sprints, and quality metrics</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setJiraEnabled(!jiraEnabled);
                }}
                className="flex items-center gap-2 text-sm"
              >
                {jiraEnabled ? (
                  <FontAwesomeIcon icon={faToggleOn} className="h-7 w-7 text-blue-400" />
                ) : (
                  <FontAwesomeIcon icon={faToggleOff} className="h-7 w-7 text-slate-500" />
                )}
              </button>
            </div>

            {/* Configuration (shown when enabled) */}
            {jiraEnabled && (
              <div className="p-5 space-y-5">

                {/* ── Atlassian OAuth2 card ── */}
                <div className="border border-blue-500/20 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-blue-500/5">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faAtlassian} className="h-3.5 w-3.5 flex-shrink-0 text-[#2684FF]" />
                      <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Atlassian OAuth2</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 font-medium">Recommended</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 font-medium">Covers Jira + Confluence</span>
                    </div>
                    {confluenceOAuthState?.isAuthenticated && (
                      <button
                        onClick={handleConfluenceDisconnect}
                        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded-lg border border-red-500/20 transition-colors"
                      >
                        <FontAwesomeIcon icon={faRightFromBracket} className="h-3 w-3" /> Disconnect
                      </button>
                    )}
                  </div>

                  {confluenceOAuthState?.isAuthenticated && confluenceOAuthState.token ? (
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-3">
                          <p className="text-gray-500 dark:text-slate-400 mb-0.5">Account</p>
                          <p className="text-gray-900 dark:text-white font-medium">{confluenceOAuthState.token.displayName ?? '—'}</p>
                          <p className="text-gray-500 dark:text-slate-400">{confluenceOAuthState.token.email ?? ''}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-3">
                          <p className="text-gray-500 dark:text-slate-400 mb-0.5">Cloud</p>
                          <p className="text-gray-900 dark:text-white font-medium truncate">{confluenceOAuthState.token.cloudName ?? confluenceOAuthState.token.cloudId ?? '—'}</p>
                          <p className="text-gray-500 dark:text-slate-400 flex items-center gap-1">
                            <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3 text-emerald-400" /> Jira + Confluence active
                          </p>
                        </div>
                      </div>
                      <p className="text-[11px] text-gray-400 dark:text-slate-500 flex items-center gap-1">
                        <FontAwesomeIcon icon={faShieldHalved} className="h-3 w-3 text-blue-400" />
                        OAuth2 token is used for both Jira and Confluence. You can also configure Confluence in the Confluence section.
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 space-y-3">
                      <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-gray-600 dark:text-slate-400 space-y-1.5">
                        <p className="font-medium text-gray-700 dark:text-slate-300 flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faLink} className="h-3 w-3 text-blue-400" /> One OAuth2 app unlocks Jira <em>and</em> Confluence
                        </p>
                        <ol className="list-decimal list-inside space-y-1 pl-1">
                          <li>Go to <a href="https://developer.atlassian.com/console/myapps/" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">developer.atlassian.com/console/myapps</a></li>
                          <li>Create app → <strong className="text-gray-700 dark:text-slate-300">OAuth 2.0 (3LO)</strong> · Add callback URL: <code className="text-blue-400 bg-blue-500/10 px-1 rounded select-all text-[10px]">{typeof window !== 'undefined' && (window as unknown as { electronAPI?: unknown }).electronAPI ? 'verigit://oauth/callback' : `${window.location.origin}/oauth/callback`}</code></li>
                          <li>Under <strong className="text-gray-700 dark:text-slate-300">Permissions</strong>, add all scopes below, then click Connect</li>
                          <li>Paste the Client ID (and optionally Secret) below, then click Connect</li>
                        </ol>
                        <div className="mt-2 space-y-1.5">
                          <p className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-wide">Confluence scopes</p>
                          <div className="flex flex-wrap gap-1">
                            {[
                              'read:confluence-space.summary','read:confluence-props','read:confluence-content.all',
                              'read:confluence-content.summary','search:confluence','read:confluence-content.permission',
                              'read:confluence-user','read:confluence-groups','readonly:content.attachment:confluence',
                              'read:space:confluence','read:space-details:confluence','read:page:confluence',
                              'read:attachment:confluence','read:comment:confluence',
                            ].map(s => <code key={s} className="text-[9px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-500 dark:text-blue-300 font-mono whitespace-nowrap">{s}</code>)}
                          </div>
                          <p className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-wide mt-1">Jira scopes</p>
                          <div className="flex flex-wrap gap-1">
                            {[
                              'read:jira-work','read:jira-user','read:sprint:jira-software','read:issue:jira-software',
                              'read:epic:jira-software','read:issue.time-tracking:jira','read:issue-event:jira',
                              'read:issue.votes:jira','read:issue.vote:jira','read:issue.transition:jira',
                              'read:issue.changelog:jira','read:issue-type-transition:jira','read:issue-status:jira',
                              'read:issue-field-values:jira','read:issue-worklog:jira','read:issue.watcher:jira',
                              'read:issue-type:jira','read:issue-details:jira','read:issue.remote-link:jira',
                              'read:issue-link-type:jira','read:issue-link:jira','read:issue-meta:jira',
                              'read:issue:jira','read:project:jira','read:jql:jira','validate:jql:jira',
                              'read:filter:jira','read:filter.column:jira','read:dev-info:jira',
                              'read:board-scope:jira-software','read:board-scope.admin:jira-software',
                            ].map(s => <code key={s} className="text-[9px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-500 dark:text-blue-300 font-mono whitespace-nowrap">{s}</code>)}
                          </div>
                          <p className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-wide mt-1">Identity &amp; access</p>
                          <div className="flex flex-wrap gap-1">
                            {['read:me','read:account','offline_access','ACT_AS_USER'].map(s => (
                              <code key={s} className="text-[9px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-500 dark:text-blue-300 font-mono whitespace-nowrap">{s}</code>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1 block">Client ID</label>
                          <input
                            type="text"
                            value={confluenceClientId}
                            onChange={(e) => setConfluenceClientId(e.target.value)}
                            placeholder="nV4LfQE4eYKLni8znefT5..."
                            className="w-full px-2.5 py-2 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1 block">Client Secret <span className="text-gray-400 font-normal">(optional)</span></label>
                          <div className="relative">
                            <input
                              type={showConfluenceSecret ? 'text' : 'password'}
                              value={confluenceClientSecret}
                              onChange={(e) => setConfluenceClientSecret(e.target.value)}
                              placeholder="ATOA3lKDkc..."
                              className="w-full px-2.5 py-2 pr-8 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                            />
                            <button type="button" onClick={() => setShowConfluenceSecret(!showConfluenceSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300">
                              {showConfluenceSecret ? <FontAwesomeIcon icon={faEyeSlash} className="h-3 w-3" /> : <FontAwesomeIcon icon={faEye} className="h-3 w-3" />}
                            </button>
                          </div>
                        </div>
                      </div>
                      {confluenceError && (
                        <div className="flex items-start gap-2 text-xs text-red-500 dark:text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg p-2.5">
                          <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> {confluenceError}
                        </div>
                      )}
                      <button
                        onClick={handleConfluenceConnect}
                        disabled={confluenceConnecting || !confluenceClientId.trim()}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {confluenceConnecting ? <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" /> : <FontAwesomeIcon icon={faLink} className="h-3.5 w-3.5" />}
                        {confluenceConnecting ? 'Opening auth window…' : 'Connect Atlassian (Jira + Confluence)'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Active auth method badge */}
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/40">
                  <span className="text-[11px] text-gray-500 dark:text-slate-400 font-medium uppercase tracking-wide">Active auth method</span>
                  {confluenceOAuthState?.isAuthenticated ? (
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      OAuth2 Bearer
                    </span>
                  ) : jiraApiToken ? (
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/25">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                      API Key (Basic)
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400 border border-gray-300 dark:border-slate-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      Not configured
                    </span>
                  )}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700/40" />
                  <span className="text-[11px] text-gray-400 dark:text-slate-500 font-medium">Or use API Token (Jira only)</span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700/40" />
                </div>

                {/* Domain */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                    <FontAwesomeIcon icon={faGlobe} className="h-3.5 w-3.5" />
                    Atlassian Domain
                  </label>
                  <div className="flex items-center">
                    <span className="px-3 py-2.5 bg-gray-200 dark:bg-slate-700/50 border border-r-0 border-gray-300 dark:border-slate-600 rounded-l-lg text-gray-500 dark:text-slate-400 text-sm">https://</span>
                    <input
                      type="text"
                      value={jiraDomain}
                      onChange={(e) => {
                        setJiraDomain(e.target.value.replace(/\.atlassian\.net\/?$/, ''));
                        setValidationResult(null);
                      }}
                      placeholder="mycompany"
                      className="flex-1 px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                    />
                    <span className="px-3 py-2.5 bg-gray-200 dark:bg-slate-700/50 border border-l-0 border-gray-300 dark:border-slate-600 rounded-r-lg text-gray-500 dark:text-slate-400 text-sm">.atlassian.net</span>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                    <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5" />
                    Atlassian Account Email
                  </label>
                  <input
                    type="email"
                    value={jiraEmail}
                    onChange={(e) => {
                      setJiraEmail(e.target.value);
                      setValidationResult(null);
                    }}
                    placeholder="you@company.com"
                    className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                  />
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                    Same Atlassian account email, but the API token below must be scoped to <strong className="text-gray-600 dark:text-slate-400">Jira</strong>.
                  </p>
                </div>

                {/* API Token */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide">
                      <FontAwesomeIcon icon={faKey} className="h-3.5 w-3.5" />
                      Jira API Token
                    </label>
                    <a
                      href="https://id.atlassian.com/manage-profile/security/api-tokens"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
                    >
                      Create token <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
                    </a>
                  </div>
                  <div className="relative">
                    <input
                      type={showJiraToken ? 'text' : 'password'}
                      value={jiraApiToken}
                      onChange={(e) => {
                        setJiraApiToken(e.target.value);
                        setValidationResult(null);
                      }}
                      placeholder="ATATT3xFfGF0..."
                      className="w-full px-3 py-2.5 pr-10 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowJiraToken(!showJiraToken)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
                    >
                      {showJiraToken ? <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4" /> : <FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Token setup instructions */}
                <div className="bg-white/90 dark:bg-slate-900/50 rounded-lg p-4 border border-gray-200 dark:border-slate-700/30 space-y-3">
                  <p className="text-xs font-medium text-gray-700 dark:text-slate-300">How to create a Jira API token:</p>
                  <ol className="text-xs text-gray-500 dark:text-slate-400 space-y-1.5 list-decimal list-inside">
                    <li>Go to <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Atlassian API Tokens</a></li>
                    <li>Click <strong className="text-gray-700 dark:text-slate-300">Create API token</strong> and name it (e.g. "VeriGit")</li>
                    <li>Copy the token and paste it above</li>
                  </ol>
                  <div className="p-2.5 bg-blue-500/5 border border-blue-500/20 rounded-lg space-y-1">
                    <p className="text-xs font-medium text-gray-700 dark:text-slate-300">Recommended: use an unscoped (classic) token</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Classic API tokens have full read access and work out of the box. If you prefer a scoped token, use the same scopes as the OAuth2 app above.</p>
                  </div>
                  <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded">
                    <p className="text-xs text-amber-600 dark:text-amber-300">
                      <strong>Note:</strong> Atlassian scopes API tokens per product. Your Bitbucket token won't work here — you need a separate Jira token.
                    </p>
                  </div>
                </div>

                {/* Validate button */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={validateJiraCredentials}
                    disabled={validating || !jiraDomain || !jiraEmail || !jiraApiToken}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    {validating ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                        Validating...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4" />
                        Test Connection
                      </>
                    )}
                  </button>

                  {validationResult && (
                    <div className={`flex items-center gap-2 text-sm ${validationResult.valid ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-400'}`}>
                      {validationResult.valid ? (
                        <>
                          <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />
                          Connected as {validationResult.user?.displayName || 'user'}
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4" />
                          {validationResult.error}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Default Board & Project (shown after validation) */}
                {validationResult?.valid && (
                  <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-slate-700/30">
                    <h3 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide">Default Configuration</h3>

                    {/* Default Project */}
                    <div>
                      <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                        <FontAwesomeIcon icon={faBuilding} className="h-3.5 w-3.5" />
                        Default Project
                      </label>
                      {loadingProjects ? (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                          Loading projects...
                        </div>
                      ) : (
                        <select
                          value={defaultProjectKey || ''}
                          onChange={(e) => setDefaultProjectKey(e.target.value || undefined)}
                          className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                        >
                          <option value="">Select a project...</option>
                          {projects.map((p) => (
                            <option key={p.key} value={p.key}>
                              {p.name} ({p.key})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Default Board */}
                    <div>
                      <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                        <FontAwesomeIcon icon={faPlug} className="h-3.5 w-3.5" />
                        Default Board
                      </label>
                      {loadingBoards ? (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                          Loading boards...
                        </div>
                      ) : (
                        <select
                          value={defaultBoardId || ''}
                          onChange={(e) => setDefaultBoardId(e.target.value ? Number(e.target.value) : undefined)}
                          className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all"
                        >
                          <option value="">Select a board...</option>
                          {boards.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name} ({b.type}) {b.location ? `- ${b.location.projectName}` : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Priority Users for Lag Indicators */}
                    <div>
                      <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                        <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5" />
                        Priority Users (shown first in Lag Indicators)
                      </label>
                      <p className="text-[10px] text-slate-500 mb-2">
                        Select up to 6 users to display first in the lag indicators table. Others appear behind "Show More".
                      </p>

                      {/* Selected priority users */}
                      {priorityUsers.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {priorityUsers.map((name, idx) => (
                            <span key={name} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/15 text-blue-600 dark:text-blue-300 border border-blue-500/20 rounded-md text-xs">
                              <span className="text-[9px] text-blue-500/60 font-mono">{idx + 1}</span>
                              {name}
                              <button
                                onClick={() => setPriorityUsers(prev => prev.filter(u => u !== name))}
                                className="ml-0.5 hover:text-red-400 transition-colors"
                              >
                                <FontAwesomeIcon icon={faMinus} className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* User picker dropdown */}
                      {loadingUsers ? (
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                          Loading users...
                        </div>
                      ) : availableUsers.length > 0 ? (
                        <div className="max-h-40 overflow-y-auto bg-white/90 dark:bg-slate-700/20 border border-gray-300 dark:border-slate-600 rounded-lg">
                          {availableUsers
                            .filter(u => !priorityUsers.includes(u.displayName))
                            .map((user) => (
                              <button
                                key={user.accountId}
                                onClick={() => {
                                  if (priorityUsers.length < 6) {
                                    setPriorityUsers(prev => [...prev, user.displayName]);
                                  }
                                }}
                                disabled={priorityUsers.length >= 6}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed border-b border-slate-700/20 last:border-b-0"
                              >
                                {user.avatarUrl && (
                                  <img src={user.avatarUrl} alt="" className="h-5 w-5 rounded-full" />
                                )}
                                <span className="text-gray-900 dark:text-white">{user.displayName}</span>
                                {user.email && <span className="text-slate-500 ml-auto">{user.email}</span>}
                                <FontAwesomeIcon icon={faPlus} className="h-3 w-3 text-blue-400 ml-auto flex-shrink-0" />
                              </button>
                            ))}
                        </div>
                      ) : defaultProjectKey ? (
                        <button
                          onClick={() => fetchProjectUsers(defaultProjectKey)}
                          className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                        >
                          <FontAwesomeIcon icon={faRotate} className="h-3 w-3" /> Load users from project
                        </button>
                      ) : (
                        <p className="text-[10px] text-slate-500">Select a default project first to load users.</p>
                      )}
                    </div>

                    <button
                      onClick={fetchBoardsAndProjects}
                      className="text-xs text-slate-400 hover:text-gray-600 dark:hover:text-slate-300 flex items-center gap-1 transition-colors"
                    >
                      <FontAwesomeIcon icon={faRotate} className="h-3 w-3" />
                      Refresh boards & projects
                    </button>
                  </div>
                )}

                {/* Save / Clear buttons */}
                <div className="flex items-center gap-3 pt-3 border-t border-gray-200 dark:border-slate-700/30">
                  <button
                    onClick={saveSettings}
                    disabled={saving}
                    className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-all flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : saveSuccess ? (
                      <>
                        <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />
                        Saved!
                      </>
                    ) : (
                      'Save Settings'
                    )}
                  </button>

                  <button
                    onClick={clearSettings}
                    className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-300 text-sm rounded-lg transition-all flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-4 w-4" />
                    Clear
                  </button>
                </div>
              </div>
            )}

            {/* Disabled state message */}
            {!jiraEnabled && (
              <div className="p-5">
                <p className="text-sm text-slate-500">
                  Enable the Jira integration to track issues, sprints, and developer quality metrics (lag indicators).
                </p>
              </div>
            )}
          </div>

          {/* Jira Status Categories */}
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden mt-5">
            <div className="p-5 border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Status Categories</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Configure which Jira statuses belong to each category. Used for colors, sorting, and indicator calculations.{statusConfig?.lastSynced && (<span className="ml-1 text-emerald-500">Last synced: {new Date(statusConfig.lastSynced).toLocaleDateString()}</span>)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleStatusSync}
                    disabled={statusSyncing || !jiraEnabled}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs rounded-lg transition-all flex items-center gap-1.5"
                    title={!jiraEnabled ? 'Enable Jira to sync statuses' : 'Fetch statuses from Jira API'}
                  >
                    <FontAwesomeIcon icon={faRotate} className={`h-3.5 w-3.5 ${statusSyncing ? 'animate-spin' : ''}`} />
                    Sync from Jira
                  </button>
                  <button
                    onClick={handleStatusReset}
                    className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-300 text-xs rounded-lg transition-all flex items-center gap-1.5"
                  >
                    <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                    Reset
                  </button>
                </div>
              </div>
              {statusSyncResult && (
                <div className={`text-xs px-3 py-2 rounded-lg mt-2 ${statusSyncResult.startsWith('Sync failed') ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                  {statusSyncResult}
                </div>
              )}
            </div>

            {statusConfig && (
              <div className="p-5 space-y-3">
                <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-3.5 w-3.5" />
                  Drag status tags between categories to re-categorize them. Click a category to add new statuses.
                </p>
                {(Object.entries(statusConfig.categories) as [StatusCategory, string[]][]).map(([category, values]) => {
                  const categoryLabels: Record<StatusCategory, { label: string; color: string }> = {
                    done: { label: 'Done', color: 'emerald' },
                    in_progress: { label: 'In Progress', color: 'blue' },
                    release_candidate: { label: 'Release Candidate', color: 'violet' },
                    testing: { label: 'Testing', color: 'amber' },
                    rework: { label: 'Rework', color: 'red' },
                    blocked: { label: 'Blocked', color: 'red' },
                    cancelled: { label: 'Cancelled', color: 'slate' },
                    hotfix: { label: 'Hotfix', color: 'orange' },
                    todo: { label: 'To Do', color: 'slate' }
};
                  const { label, color } = categoryLabels[category];
                  const isEditing = statusEditCategory === category;
                  const isDragOver = dragOverCategory === category && dragSource?.category !== category;

                  return (
                    <div
                      key={category}
                      className={`border rounded-lg overflow-hidden transition-all ${isDragOver ? 'border-blue-500 ring-2 ring-blue-500/30 bg-blue-500/5' : 'border-gray-200 dark:border-slate-700/40'}`}
                      onDragOver={(e) => handleStatusDragOver(e, category)}
                      onDragLeave={handleStatusDragLeave}
                      onDrop={() => handleStatusDrop(category)}
                    >
                      <button
                        onClick={() => setStatusEditCategory(isEditing ? null : category)}
                        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-slate-800/80 hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full bg-${color}-500`} />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{label}</span>
                          <span className="text-xs text-gray-500 dark:text-slate-400">({values.length})</span>
                        </div>
                        {isEditing ? <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-slate-400" /> : <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-slate-400" />}
                      </button>

                      <div className={`px-4 py-2 bg-white dark:bg-slate-800/30 ${isDragOver ? 'min-h-[40px]' : ''}`}>
                        <div className="flex flex-wrap gap-1.5">
                          {values.map((v, i) => (
                            <span
                              key={`${category}-${i}`}
                              draggable
                              onDragStart={() => handleStatusDragStart(category, i, v)}
                              onDragEnd={handleStatusDragEnd}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs cursor-grab active:cursor-grabbing select-none border transition-opacity ${
                                dragSource?.category === category && dragSource?.index === i
                                  ? 'opacity-30'
                                  : 'opacity-100'
                              } bg-${color}-500/10 text-${color}-700 dark:text-${color}-300 border-${color}-500/20 hover:bg-${color}-500/20`}
                            >
                              {v}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleStatusRemoveValue(category, i); }}
                                className="ml-0.5 hover:text-red-400 transition-colors"
                                title="Remove"
                              >
                                <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                          {values.length === 0 && !isDragOver && (
                            <span className="text-xs text-slate-500 italic py-1">No statuses — drag here or click to add</span>
                          )}
                          {isDragOver && (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs border-2 border-dashed border-blue-500/40 text-blue-400">
                              Drop here
                            </span>
                          )}
                        </div>
                      </div>

                      {isEditing && (
                        <div className="px-4 py-2.5 border-t border-gray-200 dark:border-slate-700/30 bg-white dark:bg-slate-800/20">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={statusNewValue}
                              onChange={(e) => setStatusNewValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleStatusAddValue(category); }}
                              placeholder="Add status name..."
                              className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600/50 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 outline-none"
                            />
                            <button
                              onClick={() => handleStatusAddValue(category)}
                              disabled={!statusNewValue.trim()}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs rounded-lg transition-all flex items-center gap-1"
                            >
                              <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                              Add
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {statusConfig.jiraStatuses.length > 0 && (
                  <div className="pt-2 border-t border-gray-200 dark:border-slate-700/30">
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">
                      {statusConfig.jiraStatuses.length} statuses known from Jira
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>)}

        {activeSection === 'linear' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Linear</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Track issues, cycles, and team activity</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5 flex items-center justify-between border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-600/20 flex items-center justify-center">
                  <img src="/brands/linear/dark.svg" className="h-5 w-5 dark:hidden" alt="" />
                  <img src="/brands/linear/light.svg" className="h-5 w-5 hidden dark:block" alt="" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Linear</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Track issues, cycles, and team activity</p>
                </div>
              </div>
              <button
                onClick={() => setLinearEnabled(!linearEnabled)}
                className="flex items-center gap-2 text-sm"
              >
                {linearEnabled ? (
                  <FontAwesomeIcon icon={faToggleOn} className="h-6 w-6 text-indigo-400" />
                ) : (
                  <FontAwesomeIcon icon={faToggleOff} className="h-6 w-6 text-slate-500" />
                )}
              </button>
            </div>

            {linearEnabled && (
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-2.5 p-3 bg-white/50 dark:bg-white/[0.04] backdrop-blur-md border border-black/[0.06] dark:border-white/[0.08] rounded-xl">
                  <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4 text-indigo-500 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Beta Integration</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Linear integration is in beta and may be prone to errors or incomplete data.</p>
                  </div>
                </div>

                {/* ── OAuth2 section ── */}
                <div className="border border-indigo-500/20 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-indigo-500/5">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faShieldHalved} className="h-3.5 w-3.5 text-indigo-400" />
                      <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Connect with OAuth2</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 font-medium">Recommended</span>
                    </div>
                    {linearOAuthState?.isAuthenticated && (
                      <button onClick={handleLinearOAuthDisconnect} className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded-lg border border-red-500/20 transition-colors">
                        <FontAwesomeIcon icon={faRightFromBracket} className="h-3 w-3" /> Disconnect
                      </button>
                    )}
                  </div>

                  {linearOAuthState?.isAuthenticated && linearOAuthState.token ? (
                    <div className="p-4 grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-3">
                        <p className="text-gray-500 dark:text-slate-400 mb-0.5">Account</p>
                        <p className="text-gray-900 dark:text-white font-medium">{linearOAuthState.token.displayName ?? '—'}</p>
                        <p className="text-gray-500 dark:text-slate-400">{linearOAuthState.token.email ?? ''}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-3">
                        <p className="text-gray-500 dark:text-slate-400 mb-0.5">Token</p>
                        <p className="text-gray-900 dark:text-white font-medium flex items-center gap-1">
                          <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3 text-emerald-400" /> OAuth2 Bearer
                        </p>
                        <p className="text-gray-500 dark:text-slate-400">No expiry · offline-capable</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 space-y-3">
                      <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-gray-600 dark:text-slate-400 space-y-1.5">
                        <p className="font-medium text-gray-700 dark:text-slate-300 flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faLink} className="h-3 w-3 text-blue-400" /> Setup: register a Linear OAuth App
                        </p>
                        <ol className="list-decimal list-inside space-y-1 pl-1">
                          <li>Go to <a href="https://linear.app/settings/api/applications/new" target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline">Linear → Settings → API → OAuth Applications</a></li>
                          <li>Create app, set callback URL: <code className="text-indigo-400 bg-indigo-500/10 px-1 rounded select-all">verigit://oauth/callback</code></li>
                          <li>Copy Client ID and Secret below, then click Connect</li>
                        </ol>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1 block">Client ID</label>
                          <input type="text" value={linearOAuthClientId} onChange={(e) => setLinearOAuthClientId(e.target.value)} placeholder="Client ID" className="w-full px-2.5 py-2 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/30" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1 block">Client Secret</label>
                          <div className="relative">
                            <input type={showLinearOAuthSecret ? 'text' : 'password'} value={linearOAuthClientSecret} onChange={(e) => setLinearOAuthClientSecret(e.target.value)} placeholder="Client Secret" className="w-full px-2.5 py-2 pr-8 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/30" />
                            <button type="button" onClick={() => setShowLinearOAuthSecret(!showLinearOAuthSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300">
                              {showLinearOAuthSecret ? <FontAwesomeIcon icon={faEyeSlash} className="h-3 w-3" /> : <FontAwesomeIcon icon={faEye} className="h-3 w-3" />}
                            </button>
                          </div>
                        </div>
                      </div>
                      {linearOAuthError && (
                        <div className="flex items-start gap-2 text-xs text-red-500 dark:text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg p-2.5">
                          <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> {linearOAuthError}
                        </div>
                      )}
                      <button onClick={handleLinearOAuthConnect} disabled={linearOAuthConnecting || !linearOAuthClientId.trim()} className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        {linearOAuthConnecting ? <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" /> : <FontAwesomeIcon icon={faLink} className="h-3.5 w-3.5" />}
                        {linearOAuthConnecting ? 'Opening auth window…' : 'Connect with Linear'}
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Divider ── */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700/40" />
                  <span className="text-[11px] text-gray-400 dark:text-slate-500 font-medium">Or use API Token</span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700/40" />
                </div>

                {/* ── PAT section ── */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                    <FontAwesomeIcon icon={faKey} className="h-3.5 w-3.5" /> API Key
                  </label>
                  <div className="relative">
                    <input
                      type={showLinearKey ? 'text' : 'password'}
                      value={linearApiKey}
                      onChange={(e) => { setLinearApiKey(e.target.value); setLinearValidation(null); }}
                      placeholder="lin_api_xxxxxxxxxxxx"
                      className="w-full px-3 py-2.5 pr-10 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500/50 transition-all"
                    />
                    <button type="button" onClick={() => setShowLinearKey(!showLinearKey)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">
                      {showLinearKey ? <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4" /> : <FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="bg-white/90 dark:bg-slate-900/50 rounded-lg p-4 border border-gray-200 dark:border-slate-700/30">
                  <p className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">How to create a Linear API key:</p>
                  <ol className="text-xs text-gray-500 dark:text-slate-400 space-y-1.5 list-decimal list-inside">
                    <li>Go to <a href="https://linear.app/settings/api" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300">Linear Settings → API</a></li>
                    <li>Click <strong className="text-gray-700 dark:text-slate-300">Create key</strong></li>
                    <li>Name it (e.g. "VeriGit") and copy the key</li>
                  </ol>
                </div>

                <div className="flex items-center gap-3">
                  <button onClick={handleValidateLinear} disabled={linearValidating || !linearApiKey} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2">
                    {linearValidating ? (<><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Validating...</>) : (<><FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4" /> Test Connection</>)}
                  </button>
                  {linearValidation && (
                    <div className={`flex items-center gap-2 text-sm ${linearValidation.valid ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-400'}`}>
                      {linearValidation.valid ? (<><FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" /> Connected as {linearValidation.user?.name || 'user'}</>) : (<><FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4" /> {linearValidation.error}</>)}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-2 border-t border-gray-200 dark:border-slate-700/30">
                  <button onClick={handleSaveLinear} disabled={linearSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-all flex items-center gap-2">
                    {linearSaving ? <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> : <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />}
                    {linearSaveSuccess ? 'Saved!' : 'Save Settings'}
                  </button>
                </div>
              </div>
            )}

            {!linearEnabled && (
              <div className="p-5">
                <p className="text-sm text-gray-400 dark:text-slate-500">Enable Linear to track issues, cycles, and team activity.</p>
              </div>
            )}
          </div>
        </>)}

        {activeSection === 'monday' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            Monday.com
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Track boards, items, and work activity</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5 flex items-center justify-between border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-pink-600/20 flex items-center justify-center">
                  <img src="/brands/monday/dark.svg" className="w-7 h-auto" alt="" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Monday.com</p>
                    {serviceConfig.monday.poc && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 dark:text-amber-400 border border-amber-500/30 font-semibold">POC</span>}
                    {serviceConfig.monday.beta && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-500 dark:text-violet-400 border border-violet-500/30 font-semibold">BETA</span>}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Track boards, items, and work activity</p>
                </div>
              </div>
              <button onClick={() => setMondayEnabled(!mondayEnabled)} className="flex items-center gap-2 text-sm">
                {mondayEnabled ? <FontAwesomeIcon icon={faToggleOn} className="h-6 w-6 text-pink-400" /> : <FontAwesomeIcon icon={faToggleOff} className="h-6 w-6 text-slate-500" />}
              </button>
            </div>

            {mondayEnabled && (
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-2.5 p-3 bg-white/50 dark:bg-white/[0.04] backdrop-blur-md border border-black/[0.06] dark:border-white/[0.08] rounded-xl">
                  <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4 text-indigo-500 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Beta Integration</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Monday.com integration is in beta and may be prone to errors or incomplete data.</p>
                  </div>
                </div>

                {/* ── OAuth2 section ── */}
                <div className="border border-pink-500/20 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-pink-500/5">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faShieldHalved} className="h-3.5 w-3.5 text-pink-400" />
                      <span className="text-xs font-medium text-gray-700 dark:text-slate-300">Connect with OAuth2</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 font-medium">Recommended</span>
                    </div>
                    {mondayOAuthState?.isAuthenticated && (
                      <button onClick={handleMondayOAuthDisconnect} className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded-lg border border-red-500/20 transition-colors">
                        <FontAwesomeIcon icon={faRightFromBracket} className="h-3 w-3" /> Disconnect
                      </button>
                    )}
                  </div>

                  {mondayOAuthState?.isAuthenticated && mondayOAuthState.token ? (
                    <div className="p-4 grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-3">
                        <p className="text-gray-500 dark:text-slate-400 mb-0.5">Account</p>
                        <p className="text-gray-900 dark:text-white font-medium">{mondayOAuthState.token.displayName ?? '—'}</p>
                        <p className="text-gray-500 dark:text-slate-400">{mondayOAuthState.token.email ?? ''}</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-3">
                        <p className="text-gray-500 dark:text-slate-400 mb-0.5">Token</p>
                        <p className="text-gray-900 dark:text-white font-medium flex items-center gap-1">
                          <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3 text-emerald-400" /> OAuth2 Bearer
                        </p>
                        <p className="text-gray-500 dark:text-slate-400">Auto-refresh · offline-capable</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 space-y-3">
                      <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-gray-600 dark:text-slate-400 space-y-1.5">
                        <p className="font-medium text-gray-700 dark:text-slate-300 flex items-center gap-1.5">
                          <FontAwesomeIcon icon={faLink} className="h-3 w-3 text-blue-400" /> Setup: register a Monday.com OAuth App
                        </p>
                        <ol className="list-decimal list-inside space-y-1 pl-1">
                          <li>Go to <a href="https://monday.com/developers/apps" target="_blank" rel="noreferrer" className="text-pink-400 hover:underline">Monday Developer Center → Apps</a></li>
                          <li>Create app → OAuth, set redirect URI: <code className="text-pink-400 bg-pink-500/10 px-1 rounded select-all">verigit://oauth/callback</code></li>
                          <li>Copy Client ID and Secret below, then click Connect</li>
                        </ol>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1 block">Client ID</label>
                          <input type="text" value={mondayOAuthClientId} onChange={(e) => setMondayOAuthClientId(e.target.value)} placeholder="Client ID" className="w-full px-2.5 py-2 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-pink-500/30" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1 block">Client Secret</label>
                          <div className="relative">
                            <input type={showMondayOAuthSecret ? 'text' : 'password'} value={mondayOAuthClientSecret} onChange={(e) => setMondayOAuthClientSecret(e.target.value)} placeholder="Client Secret" className="w-full px-2.5 py-2 pr-8 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-pink-500/30" />
                            <button type="button" onClick={() => setShowMondayOAuthSecret(!showMondayOAuthSecret)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300">
                              {showMondayOAuthSecret ? <FontAwesomeIcon icon={faEyeSlash} className="h-3 w-3" /> : <FontAwesomeIcon icon={faEye} className="h-3 w-3" />}
                            </button>
                          </div>
                        </div>
                      </div>
                      {mondayOAuthError && (
                        <div className="flex items-start gap-2 text-xs text-red-500 dark:text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg p-2.5">
                          <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> {mondayOAuthError}
                        </div>
                      )}
                      <button onClick={handleMondayOAuthConnect} disabled={mondayOAuthConnecting || !mondayOAuthClientId.trim()} className="flex items-center gap-2 px-3 py-2 bg-pink-600 hover:bg-pink-500 text-white text-xs font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                        {mondayOAuthConnecting ? <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" /> : <FontAwesomeIcon icon={faLink} className="h-3.5 w-3.5" />}
                        {mondayOAuthConnecting ? 'Opening auth window…' : 'Connect with Monday.com'}
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Divider ── */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700/40" />
                  <span className="text-[11px] text-gray-400 dark:text-slate-500 font-medium">Or use API Token</span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700/40" />
                </div>

                {/* ── PAT section ── */}
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                    <FontAwesomeIcon icon={faKey} className="h-3.5 w-3.5" /> API Token
                  </label>
                  <div className="relative">
                    <input
                      type={showMondayKey ? 'text' : 'password'}
                      value={mondayApiKey}
                      onChange={(e) => { setMondayApiKey(e.target.value); setMondayValidation(null); }}
                      placeholder="eyJhbGciOiJIUz..."
                      className="w-full px-3 py-2.5 pr-10 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/30 focus:border-pink-500/50 transition-all"
                    />
                    <button type="button" onClick={() => setShowMondayKey(!showMondayKey)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">
                      {showMondayKey ? <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4" /> : <FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="bg-white/90 dark:bg-slate-900/50 rounded-lg p-4 border border-gray-200 dark:border-slate-700/30">
                  <p className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">How to create a Monday API token:</p>
                  <ol className="text-xs text-gray-500 dark:text-slate-400 space-y-1.5 list-decimal list-inside">
                    <li>Go to <a href="https://monday.com" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:text-pink-300">Monday.com</a> → Profile → Admin → API</li>
                    <li>Copy your <strong className="text-gray-700 dark:text-slate-300">API v2 Token</strong></li>
                  </ol>
                </div>

                <div className="flex items-center gap-3">
                  <button onClick={handleValidateMonday} disabled={mondayValidating || !mondayApiKey} className="px-4 py-2 bg-pink-600 hover:bg-pink-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2">
                    {mondayValidating ? (<><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Validating...</>) : (<><FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4" /> Test Connection</>)}
                  </button>
                  {mondayValidation && (
                    <div className={`flex items-center gap-2 text-sm ${mondayValidation.valid ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-400'}`}>
                      {mondayValidation.valid ? (<><FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" /> Connected as {mondayValidation.user?.name || 'user'}</>) : (<><FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4" /> {mondayValidation.error}</>)}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-2 border-t border-gray-200 dark:border-slate-700/30">
                  <button onClick={handleSaveMonday} disabled={mondaySaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-all flex items-center gap-2">
                    {mondaySaving ? <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> : <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />}
                    {mondaySaveSuccess ? 'Saved!' : 'Save Settings'}
                  </button>
                </div>
              </div>
            )}

            {!mondayEnabled && (
              <div className="p-5">
                <p className="text-sm text-gray-400 dark:text-slate-500">Enable Monday.com to track boards, items, and work activity.</p>
              </div>
            )}
          </div>
        </>)}

        {activeSection === 'trello' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            Trello
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Track boards, cards, and team activity</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5 flex items-center justify-between border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-600/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faTrello} className="h-5 w-5 text-sky-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Trello</p>
                    {serviceConfig.trello.poc && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 dark:text-amber-400 border border-amber-500/30 font-semibold">POC</span>}
                    {serviceConfig.trello.beta && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-500 dark:text-violet-400 border border-violet-500/30 font-semibold">BETA</span>}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Track boards, cards, and team activity</p>
                </div>
              </div>
              <button onClick={() => setTrelloEnabled(!trelloEnabled)} className="flex items-center gap-2 text-sm">
                {trelloEnabled ? <FontAwesomeIcon icon={faToggleOn} className="h-6 w-6 text-sky-400" /> : <FontAwesomeIcon icon={faToggleOff} className="h-6 w-6 text-slate-500" />}
              </button>
            </div>

            {trelloEnabled && (
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-2.5 p-3 bg-white/50 dark:bg-white/[0.04] backdrop-blur-md border border-black/[0.06] dark:border-white/[0.08] rounded-xl">
                  <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4 text-indigo-500 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Beta Integration</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Trello integration is in beta and may be prone to errors or incomplete data.</p>
                  </div>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                    <FontAwesomeIcon icon={faKey} className="h-3.5 w-3.5" /> API Key
                  </label>
                  <input
                    type="text"
                    value={trelloApiKey}
                    onChange={(e) => { setTrelloApiKey(e.target.value); setTrelloValidation(null); }}
                    placeholder="Your Trello API key"
                    className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500/50 transition-all"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                    <FontAwesomeIcon icon={faKey} className="h-3.5 w-3.5" /> Token
                  </label>
                  <div className="relative">
                    <input
                      type={showTrelloKey ? 'text' : 'password'}
                      value={trelloToken}
                      onChange={(e) => { setTrelloToken(e.target.value); setTrelloValidation(null); }}
                      placeholder="Your Trello token"
                      className="w-full px-3 py-2.5 pr-10 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500/50 transition-all"
                    />
                    <button type="button" onClick={() => setShowTrelloKey(!showTrelloKey)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">
                      {showTrelloKey ? <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4" /> : <FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="bg-white/90 dark:bg-slate-900/50 rounded-lg p-4 border border-gray-200 dark:border-slate-700/30">
                  <p className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">How to get Trello credentials:</p>
                  <ol className="text-xs text-gray-500 dark:text-slate-400 space-y-1.5 list-decimal list-inside">
                    <li>Get your API key from <a href="https://trello.com/power-ups/admin" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300">Trello Power-Ups Admin</a></li>
                    <li>Generate a <strong className="text-gray-700 dark:text-slate-300">Token</strong> from the same page</li>
                  </ol>
                </div>

                <div className="flex items-center gap-3">
                  <button onClick={handleValidateTrello} disabled={trelloValidating || !trelloApiKey || !trelloToken} className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2">
                    {trelloValidating ? (<><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Validating...</>) : (<><FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4" /> Test Connection</>)}
                  </button>
                  {trelloValidation && (
                    <div className={`flex items-center gap-2 text-sm ${trelloValidation.valid ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-400'}`}>
                      {trelloValidation.valid ? (<><FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" /> Connected as {trelloValidation.user?.fullName || 'user'}</>) : (<><FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4" /> {trelloValidation.error}</>)}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-2 border-t border-gray-200 dark:border-slate-700/30">
                  <button onClick={handleSaveTrello} disabled={trelloSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-all flex items-center gap-2">
                    {trelloSaving ? <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> : <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />}
                    {trelloSaveSuccess ? 'Saved!' : 'Save Settings'}
                  </button>
                </div>
              </div>
            )}

            {!trelloEnabled && (
              <div className="p-5">
                <p className="text-sm text-gray-400 dark:text-slate-500">Enable Trello to track boards, cards, and team activity.</p>
              </div>
            )}
          </div>
        </>)}

        {activeSection === 'confluence' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Confluence</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Connect your Atlassian Confluence workspace to track documentation activity, page versions, and comments.</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            {/* Header row */}
            <div className="p-5 flex items-center justify-between border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faGlobe} className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    Confluence
                    {confluenceOAuthState?.isAuthenticated && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
                        <FontAwesomeIcon icon={faCircleCheck} className="h-2.5 w-2.5" /> Connected
                      </span>
                    )}
                    {confluenceOAuthState?.isExpired && confluenceOAuthState?.canRefresh && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30">
                        <FontAwesomeIcon icon={faRotate} className="h-2.5 w-2.5" /> Token expiring
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    OAuth 2.0 (3LO) · Offline-capable with token refresh
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setConfluenceEnabled(!confluenceEnabled);
                    // Save the setting
                    getConfluenceSettings().then(s => {
                      saveConfluenceSettings({
                        ...s,
                        enabled: !confluenceEnabled,
                        cloudId: s?.cloudId || '',
                        cloudName: s?.cloudName || '',
                        clientId: s?.clientId || '',
                        clientSecret: s?.clientSecret || ''
                      });
                    });
                    onSettingsChanged?.();
                  }}
                  className="flex items-center gap-2 text-sm"
                  disabled={!confluenceOAuthState?.isAuthenticated}
                >
                  {confluenceEnabled ? (
                    <FontAwesomeIcon icon={faToggleOn} className="h-7 w-7 text-blue-400" />
                  ) : (
                    <FontAwesomeIcon icon={faToggleOff} className="h-7 w-7 text-slate-500" />
                  )}
                </button>
                {confluenceOAuthState?.isAuthenticated && (
                  <button
                    onClick={handleConfluenceDisconnect}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-500/10 rounded-lg border border-red-500/20 transition-colors"
                  >
                    <FontAwesomeIcon icon={faRightFromBracket} className="h-3.5 w-3.5" />
                    Disconnect
                  </button>
                )}
              </div>
            </div>

            {/* Connected state */}
            {confluenceOAuthState?.isAuthenticated && confluenceOAuthState.token && (
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-3">
                    <p className="text-gray-500 dark:text-slate-400 mb-0.5">Account</p>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {confluenceOAuthState.token.displayName ?? '—'}
                    </p>
                    <p className="text-gray-500 dark:text-slate-400">{confluenceOAuthState.token.email ?? ''}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-3">
                    <p className="text-gray-500 dark:text-slate-400 mb-0.5">Cloud</p>
                    <p className="text-gray-900 dark:text-white font-medium truncate">
                      {confluenceOAuthState.token.cloudName ?? confluenceOAuthState.token.cloudId ?? '—'}
                    </p>
                    <p className="text-gray-500 dark:text-slate-400">
                      Expires: {new Date(confluenceOAuthState.token.expiresAt).toLocaleTimeString()}
                    </p>
                  </div>
                </div>

                {/* Connection capabilities */}
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {[
                    { icon: <FontAwesomeIcon icon={faWifi} className="h-3.5 w-3.5 text-blue-400" />, label: 'Live sync', desc: 'Real-time page updates' },
                    { icon: <FontAwesomeIcon icon={faDatabase} className="h-3.5 w-3.5 text-purple-400" />, label: 'Offline cache', desc: 'Works without internet' },
                    { icon: <FontAwesomeIcon icon={faShieldHalved} className="h-3.5 w-3.5 text-emerald-400" />, label: 'OAuth 2.0', desc: 'Secure token refresh' },
                  ].map(cap => (
                    <div key={cap.label} className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-2.5 flex flex-col gap-1">
                      {cap.icon}
                      <p className="font-medium text-gray-700 dark:text-slate-300">{cap.label}</p>
                      <p className="text-gray-400 dark:text-slate-500">{cap.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Not connected state — show setup instructions */}
            {!confluenceOAuthState?.isAuthenticated && (
              <div className="p-5 space-y-4">
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4 text-xs text-gray-600 dark:text-slate-400 space-y-2">
                  <p className="font-medium text-gray-700 dark:text-slate-300 flex items-center gap-1.5">
                    <FontAwesomeIcon icon={faLink} className="h-3.5 w-3.5 text-blue-400" />
                    Setup required — register an OAuth2 app first
                  </p>
                  <ol className="list-decimal list-inside space-y-1 pl-1">
                    <li>Go to <a href="https://developer.atlassian.com/console/myapps/" target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">developer.atlassian.com/console/myapps</a></li>
                    <li>Create app → OAuth 2.0 (3LO)</li>
                    <li>Add callback URL: <code className="text-blue-400 bg-blue-500/10 px-1 rounded select-all">{getAtlassianRedirectUri()}</code></li>
                    <li>Under <strong className="text-gray-700 dark:text-slate-300">Permissions</strong>, add all scopes below</li>
                    <li>Copy the Client ID below and click Connect</li>
                  </ol>
                  <div className="mt-2 space-y-1.5">
                    <p className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-wide">Confluence scopes</p>
                    <div className="flex flex-wrap gap-1">
                      {[
                        'read:confluence-space.summary','read:confluence-props','read:confluence-content.all',
                        'read:confluence-content.summary','search:confluence','read:confluence-content.permission',
                        'read:confluence-user','read:confluence-groups','readonly:content.attachment:confluence',
                        'read:space:confluence','read:space-details:confluence','read:page:confluence',
                        'read:attachment:confluence','read:comment:confluence',
                      ].map(s => <code key={s} className="text-[9px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-500 dark:text-blue-300 font-mono whitespace-nowrap">{s}</code>)}
                    </div>
                    <p className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-wide mt-1">Jira scopes</p>
                    <div className="flex flex-wrap gap-1">
                      {[
                        'read:jira-work','read:jira-user','read:sprint:jira-software','read:issue:jira-software',
                        'read:epic:jira-software','read:issue.time-tracking:jira','read:issue-event:jira',
                        'read:issue.votes:jira','read:issue.vote:jira','read:issue.transition:jira',
                        'read:issue.changelog:jira','read:issue-type-transition:jira','read:issue-status:jira',
                        'read:issue-field-values:jira','read:issue-worklog:jira','read:issue.watcher:jira',
                        'read:issue-type:jira','read:issue-details:jira','read:issue.remote-link:jira',
                        'read:issue-link-type:jira','read:issue-link:jira','read:issue-meta:jira',
                        'read:issue:jira','read:project:jira','read:jql:jira','validate:jql:jira',
                        'read:filter:jira','read:filter.column:jira','read:dev-info:jira',
                        'read:board-scope:jira-software','read:board-scope.admin:jira-software',
                      ].map(s => <code key={s} className="text-[9px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-500 dark:text-blue-300 font-mono whitespace-nowrap">{s}</code>)}
                    </div>
                    <p className="text-[10px] font-semibold text-gray-500 dark:text-slate-500 uppercase tracking-wide mt-1">Identity &amp; access</p>
                    <div className="flex flex-wrap gap-1">
                      {['read:me','read:account','offline_access','ACT_AS_USER'].map(s => (
                        <code key={s} className="text-[9px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-500 dark:text-blue-300 font-mono whitespace-nowrap">{s}</code>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1.5 block">OAuth2 Client ID</label>
                    <input
                      type="text"
                      value={confluenceClientId}
                      onChange={(e) => setConfluenceClientId(e.target.value)}
                      placeholder="e.g. nV4LfQE4eYKLni8znefT5mQdUOkcPCXs"
                      className="w-full px-3 py-2 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1.5 block">OAuth2 Secret</label>
                    <div className="relative">
                      <input
                        type={showConfluenceSecret ? 'text' : 'password'}
                        value={confluenceClientSecret}
                        onChange={(e) => setConfluenceClientSecret(e.target.value)}
                        placeholder="e.g. ATOA3lKDkc..."
                        className="w-full px-3 py-2 pr-9 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfluenceSecret(!showConfluenceSecret)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
                      >
                        {showConfluenceSecret ? <FontAwesomeIcon icon={faEyeSlash} className="h-3.5 w-3.5" /> : <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-1">
                      Client ID and Secret are found in your app settings at developer.atlassian.com. Stored locally in IndexedDB only.
                    </p>
                  </div>
                </div>

                {confluenceError && (
                  <div className="flex items-start gap-2 text-xs text-red-500 dark:text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                    <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    {confluenceError}
                  </div>
                )}

                <button
                  onClick={handleConfluenceConnect}
                  disabled={confluenceConnecting || !confluenceClientId.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {confluenceConnecting ? (
                    <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faLink} className="h-4 w-4" />
                  )}
                  {confluenceConnecting ? 'Opening auth window…' : 'Connect with Atlassian'}
                </button>
              </div>
            )}
          </div>
        </>)}

        {activeSection === 'advanced' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Advanced</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Customize indicator code and git author aliases</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faFileCode} className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Indicator Code Editor</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Customize the JavaScript code that calculates each health indicator (lag & lead metrics)</p>
                </div>
              </div>
              <button
                onClick={() => onNavigateToIndicatorEditor ? onNavigateToIndicatorEditor() : setShowIndicatorEditor(true)}
                className="px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 text-purple-700 dark:text-purple-300 text-sm rounded-lg transition-all flex items-center gap-2 border border-purple-500/20"
              >
                <FontAwesomeIcon icon={faFileCode} className="h-4 w-4" />
                Open Editor
              </button>
            </div>
          </div>
        </>)}

        {activeSection === 'notion' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            Notion
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Track Notion pages, databases, and documentation activity — similar to Confluence integration</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5 flex items-center justify-between border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-800/20 dark:bg-white/10 flex items-center justify-center">
                  <FontAwesomeIcon icon={faNotion} className="h-5 w-5 text-gray-700 dark:text-gray-200" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Notion Workspace</p>
                    {serviceConfig.notion.poc && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 dark:text-amber-400 border border-amber-500/30 font-semibold">POC</span>}
                    {serviceConfig.notion.beta && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-500 dark:text-violet-400 border border-violet-500/30 font-semibold">BETA</span>}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Connect via Internal Integration Token</p>
                </div>
              </div>
              <button onClick={() => setNotionEnabled(!notionEnabled)} className="flex items-center gap-2 text-sm">
                {notionEnabled ? <FontAwesomeIcon icon={faToggleOn} className="h-6 w-6 text-gray-700 dark:text-gray-300" /> : <FontAwesomeIcon icon={faToggleOff} className="h-6 w-6 text-slate-500" />}
              </button>
            </div>

            {notionEnabled && (
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-2.5 p-3 bg-white/50 dark:bg-white/[0.04] backdrop-blur-md border border-black/[0.06] dark:border-white/[0.08] rounded-xl">
                  <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4 text-violet-500 dark:text-violet-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Beta Integration</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Notion integration is in beta. Create an <a href="https://www.notion.so/my-integrations" target="_blank" rel="noreferrer" className="text-violet-400 hover:underline">Internal Integration</a> and share relevant pages/databases with it.</p>
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                    <FontAwesomeIcon icon={faKey} className="h-3.5 w-3.5" /> Internal Integration Token
                  </label>
                  <div className="relative">
                    <input
                      type={showNotionKey ? 'text' : 'password'}
                      value={notionApiKey}
                      onChange={(e) => { setNotionApiKey(e.target.value); setNotionValidation(null); }}
                      placeholder="ntn_..."
                      className="w-full px-3 py-2.5 pr-10 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500/50 transition-all font-mono"
                    />
                    <button type="button" onClick={() => setShowNotionKey(!showNotionKey)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-gray-600 dark:hover:text-slate-300">
                      {showNotionKey ? <FontAwesomeIcon icon={faEyeSlash} className="h-4 w-4" /> : <FontAwesomeIcon icon={faEye} className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button onClick={handleValidateNotion} disabled={notionValidating || !notionApiKey} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 dark:bg-white/10 dark:hover:bg-white/15 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2">
                    {notionValidating ? (<><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Validating...</>) : (<><FontAwesomeIcon icon={faShieldHalved} className="h-4 w-4" /> Test Connection</>)}
                  </button>
                  {notionValidation && (
                    <div className={`flex items-center gap-2 text-sm ${notionValidation.valid ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-400'}`}>
                      {notionValidation.valid ? (<><FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" /> {notionValidation.workspaceName || 'Connected'}</>) : (<><FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4" /> {notionValidation.error}</>)}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-2 border-t border-gray-200 dark:border-slate-700/30">
                  <button onClick={handleSaveNotion} disabled={notionSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-all flex items-center gap-2">
                    {notionSaving ? <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> : <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />}
                    {notionSaveSuccess ? 'Saved!' : 'Save Settings'}
                  </button>
                </div>
              </div>
            )}

            {!notionEnabled && (
              <div className="p-5">
                <p className="text-sm text-gray-400 dark:text-slate-500">Enable Notion to track pages, databases, and documentation activity in Combined View.</p>
              </div>
            )}
          </div>
        </>)}

        {activeSection === 'google-docs' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            Google Docs
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Track Google Docs, Sheets, and Slides activity — similar to Confluence integration</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5 flex items-center justify-between border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faGoogle} className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Google Workspace</p>
                    {serviceConfig['google-docs'].poc && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 dark:text-amber-400 border border-amber-500/30 font-semibold">POC</span>}
                    {serviceConfig['google-docs'].beta && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-500 dark:text-violet-400 border border-violet-500/30 font-semibold">BETA</span>}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Connect via Google OAuth2</p>
                </div>
              </div>
              <button onClick={() => setGoogleDocsEnabled(!googleDocsEnabled)} className="flex items-center gap-2 text-sm">
                {googleDocsEnabled ? <FontAwesomeIcon icon={faToggleOn} className="h-6 w-6 text-blue-500" /> : <FontAwesomeIcon icon={faToggleOff} className="h-6 w-6 text-slate-500" />}
              </button>
            </div>

            {googleDocsEnabled && (
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-2.5 p-3 bg-white/50 dark:bg-white/[0.04] backdrop-blur-md border border-black/[0.06] dark:border-white/[0.08] rounded-xl">
                  <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4 text-violet-500 dark:text-violet-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Beta Integration — OAuth2 Required</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Google Docs integration requires OAuth2 authentication. You will need to set up a Google Cloud project with the Drive API enabled and configure OAuth2 consent screen.</p>
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">
                    <FontAwesomeIcon icon={faFolderOpen} className="h-3.5 w-3.5" /> Drive Folder ID (optional)
                  </label>
                  <input
                    type="text"
                    value={googleDocsFolderId}
                    onChange={(e) => setGoogleDocsFolderId(e.target.value)}
                    placeholder="Leave empty to scan all accessible documents"
                    className="w-full px-3 py-2.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all font-mono"
                  />
                </div>

                <div className="bg-white/90 dark:bg-slate-900/50 rounded-lg p-4 border border-gray-200 dark:border-slate-700/30">
                  <p className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">How to set up Google Docs integration:</p>
                  <ol className="text-xs text-gray-500 dark:text-slate-400 space-y-1.5 list-decimal list-inside">
                    <li>Create a project in <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Google Cloud Console</a></li>
                    <li>Enable the <strong className="text-gray-700 dark:text-slate-300">Google Drive API</strong></li>
                    <li>Configure the OAuth2 consent screen and create credentials</li>
                    <li>OAuth2 flow will be available once configured</li>
                  </ol>
                </div>

                <div className="flex items-center gap-3 pt-2 border-t border-gray-200 dark:border-slate-700/30">
                  <button onClick={handleSaveGoogleDocs} disabled={googleDocsSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-all flex items-center gap-2">
                    {googleDocsSaving ? <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> : <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />}
                    {googleDocsSaveSuccess ? 'Saved!' : 'Save Settings'}
                  </button>
                </div>
              </div>
            )}

            {!googleDocsEnabled && (
              <div className="p-5">
                <p className="text-sm text-gray-400 dark:text-slate-500">Enable Google Docs to track document activity in Combined View.</p>
              </div>
            )}
          </div>
        </>)}

        {activeSection === 'slack' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            <FontAwesomeIcon icon={faSlack} className="h-5 w-5 text-[#E01E5A]" />Slack
            <span className="text-[9px] px-1.5 rounded bg-violet-500/20 text-violet-500 dark:text-violet-400 border border-violet-500/30 font-semibold">BETA</span>
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Monitor Slack channels for activity, anomalies, and daily summaries</p>

          {/* ── Connection card ── */}
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden mb-4">
            <div className="p-5 space-y-5">
              {/* Enable toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Enable Slack monitoring</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Poll joined channels every 5 minutes</p>
                </div>
                <button onClick={() => setSlackEnabled(v => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${slackEnabled ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-slate-600'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${slackEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* Bot token */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600 dark:text-slate-300 block">Bot Token <span className="text-gray-400">(xoxb-...)</span></label>
                <div className="flex gap-2">
                  <input
                    type={showSlackToken ? 'text' : 'password'}
                    value={slackBotToken}
                    onChange={e => { setSlackBotToken(e.target.value); setSlackChannels([]); }}
                    placeholder="xoxb-..."
                    className="flex-1 px-3 py-2 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500/50 transition-all"
                  />
                  <button onClick={() => setShowSlackToken(v => !v)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-xs">
                    {showSlackToken ? 'Hide' : 'Show'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 dark:text-slate-500">
                  Create a Slack app at <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">api.slack.com/apps</a> and install it to your workspace. Required OAuth scopes: <code className="text-indigo-400">channels:history channels:read channels:join groups:history groups:read users:read chat:write reactions:read</code>
                </p>
              </div>

              {/* Save */}
              <div className="flex items-center gap-3 pt-2 border-t border-gray-200 dark:border-slate-700/30">
                <button onClick={handleSaveSlack} disabled={slackSaving}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-all flex items-center gap-2">
                  {slackSaving ? <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> : <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />}
                  {slackSaveSuccess ? 'Saved!' : 'Save Settings'}
                </button>
                {slackSaveSuccess && <span className="text-sm text-emerald-500 flex items-center gap-1"><FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5" />Settings saved</span>}
              </div>
            </div>
          </div>

          {/* ── Channel browser ── */}
          {slackBotToken.trim() && (
            <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
              {/* Header row */}
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 dark:border-slate-700/30">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Workspace channels</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                    Join public channels so the bot can read them, then toggle monitoring
                  </p>
                </div>
                <button
                  onClick={loadSlackChannels}
                  disabled={slackChannelsLoading}
                  className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-all"
                >
                  {slackChannelsLoading
                    ? <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />
                    : <FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-3.5 w-3.5" />}
                  {slackChannels.length > 0 ? 'Refresh' : 'Load channels'}
                </button>
              </div>

              {slackChannelsError && (
                <div className="px-5 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/30">
                  <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5">
                    <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5" />{slackChannelsError}
                  </p>
                </div>
              )}

              {slackChannels.length > 0 && (
                <>
                  {/* Search + summary */}
                  <div className="px-5 py-3 border-b border-gray-100 dark:border-slate-700/20 flex items-center gap-3">
                    <input
                      type="text"
                      value={slackChannelSearch}
                      onChange={e => setSlackChannelSearch(e.target.value)}
                      placeholder="Search channels…"
                      className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500/50 transition-all"
                    />
                    <span className="text-xs text-gray-400 dark:text-slate-500 whitespace-nowrap">
                      {slackChannels.filter(c => c.isMember).length} joined · {slackMonitoredIds.size} monitored
                    </span>
                  </div>

                  {/* Channel list */}
                  <div className="divide-y divide-gray-100 dark:divide-slate-700/20 max-h-96 overflow-y-auto">
                    {slackChannels
                      .filter(ch => !slackChannelSearch || ch.name.toLowerCase().includes(slackChannelSearch.toLowerCase()))
                      .map(ch => (
                        <div key={ch.id} className="flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                          {/* Channel name + badges */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-mono text-gray-700 dark:text-slate-200 truncate">
                                {ch.isPrivate ? '🔒' : '#'}{ch.name}
                              </span>
                              {ch.isMember && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 font-semibold whitespace-nowrap">JOINED</span>
                              )}
                              {ch.memberCount !== undefined && (
                                <span className="text-[10px] text-gray-400 dark:text-slate-500">{ch.memberCount} members</span>
                              )}
                            </div>
                            {ch.topic && <p className="text-[10px] text-gray-400 dark:text-slate-500 truncate mt-0.5">{ch.topic}</p>}
                          </div>

                          {/* Monitor toggle (only for joined channels) */}
                          {ch.isMember && (
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="text-[10px] text-gray-400 dark:text-slate-500">Monitor</span>
                              <button
                                onClick={() => setSlackMonitoredIds(prev => {
                                  const n = new Set(prev);
                                  if (n.has(ch.id)) n.delete(ch.id); else n.add(ch.id);
                                  return n;
                                })}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${slackMonitoredIds.has(ch.id) ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-slate-600'}`}
                              >
                                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${slackMonitoredIds.has(ch.id) ? 'translate-x-4' : 'translate-x-0.5'}`} />
                              </button>
                            </div>
                          )}

                          {/* Join / Leave button */}
                          {!ch.isPrivate && (
                            ch.isMember ? (
                              <button
                                onClick={() => handleLeaveSlackChannel(ch.id)}
                                disabled={slackLeavingId === ch.id}
                                className="flex-shrink-0 px-2.5 py-1 text-[10px] font-medium rounded-md border border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:border-red-400 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-50 transition-all"
                              >
                                {slackLeavingId === ch.id ? <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> : 'Leave'}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleJoinSlackChannel(ch.id)}
                                disabled={slackJoiningId === ch.id}
                                className="flex-shrink-0 px-2.5 py-1 text-[10px] font-medium rounded-md bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50 transition-all"
                              >
                                {slackJoiningId === ch.id ? <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> : 'Join'}
                              </button>
                            )
                          )}
                          {ch.isPrivate && !ch.isMember && (
                            <span className="flex-shrink-0 text-[10px] text-gray-400 dark:text-slate-500 italic">Invite only</span>
                          )}
                        </div>
                      ))}
                    {slackChannels.filter(ch => !slackChannelSearch || ch.name.toLowerCase().includes(slackChannelSearch.toLowerCase())).length === 0 && (
                      <div className="px-5 py-6 text-center text-xs text-gray-400 dark:text-slate-500">No channels match "{slackChannelSearch}"</div>
                    )}
                  </div>

                  {/* Save monitored selection footer */}
                  <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-200 dark:border-slate-700/30 bg-gray-50/50 dark:bg-slate-900/20">
                    <p className="flex-1 text-xs text-gray-500 dark:text-slate-400">
                      {slackMonitoredIds.size === 0
                        ? 'No channels selected — all joined channels will be polled'
                        : `${slackMonitoredIds.size} channel${slackMonitoredIds.size === 1 ? '' : 's'} selected for monitoring`}
                    </p>
                    <button onClick={handleSaveSlack} disabled={slackSaving}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium rounded-lg disabled:opacity-50 transition-all flex items-center gap-1.5">
                      {slackSaving ? <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> : <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" />}
                      {slackSaveSuccess ? 'Saved!' : 'Save selection'}
                    </button>
                  </div>
                </>
              )}

              {!slackChannelsLoading && slackChannels.length === 0 && !slackChannelsError && (
                <div className="px-5 py-6 text-center">
                  <p className="text-xs text-gray-400 dark:text-slate-500">Click <strong>Load channels</strong> to browse your workspace and choose which channels to join and monitor.</p>
                </div>
              )}
            </div>
          )}
        </>)}

        {activeSection === 'teams' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            <FontAwesomeIcon icon={faMicrosoft} className="h-5 w-5 text-[#6264A7]" />Microsoft Teams
            <span className="text-[9px] px-1.5 rounded bg-violet-500/20 text-violet-500 dark:text-violet-400 border border-violet-500/30 font-semibold">BETA</span>
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Monitor Microsoft Teams channels for activity, anomalies, and daily summaries</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5 space-y-5">
              {/* Enable toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Enable Teams monitoring</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Poll joined Teams channels every 5 minutes via Microsoft Graph API</p>
                </div>
                <button onClick={() => setTeamsEnabled(v => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${teamsEnabled ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-slate-600'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${teamsEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* Access token */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600 dark:text-slate-300 block">Access Token</label>
                <div className="flex gap-2">
                  <input
                    type={showTeamsToken ? 'text' : 'password'}
                    value={teamsAccessToken}
                    onChange={e => setTeamsAccessToken(e.target.value)}
                    placeholder="eyJ0eXAiOiJKV1QiLCJhbGci..."
                    className="flex-1 px-3 py-2 bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500/50 transition-all"
                  />
                  <button onClick={() => setShowTeamsToken(v => !v)} className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-xs">
                    {showTeamsToken ? 'Hide' : 'Show'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 dark:text-slate-500">
                  Register an app in <a href="https://portal.azure.com" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">Azure Portal</a> with delegated permissions: <code className="text-indigo-400">Team.ReadBasic.All Channel.ReadBasic.All ChannelMessage.Read.All User.Read.All ChannelMessage.Send</code>. Paste the acquired access token here.
                </p>
              </div>

              {/* Save */}
              <div className="flex items-center gap-3 pt-2 border-t border-gray-200 dark:border-slate-700/30">
                <button onClick={handleSaveTeams} disabled={teamsSaving}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-all flex items-center gap-2">
                  {teamsSaving ? <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> : <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />}
                  {teamsSaveSuccess ? 'Saved!' : 'Save Settings'}
                </button>
                {teamsSaveSuccess && <span className="text-sm text-emerald-500 flex items-center gap-1"><FontAwesomeIcon icon={faCircleCheck} className="h-3.5 w-3.5" />Settings saved</span>}
              </div>
            </div>
          </div>
        </>)}

        {activeSection === 'notifications' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Notifications</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Control push notification behaviour</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            {/* Master toggle */}
            <div className="p-5 flex items-center justify-between border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-600/20 flex items-center justify-center">
                  {notifSettings.enabled ? <FontAwesomeIcon icon={faBell} className="h-5 w-5 text-cyan-400" /> : <FontAwesomeIcon icon={faBellSlash} className="h-5 w-5 text-slate-400" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Push Notifications</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Get notified when analyses finish so you can tab away</p>
                </div>
              </div>
              <button
                onClick={() => {
                  const updated = { ...notifSettings, enabled: !notifSettings.enabled };
                  setNotifSettings(updated);
                  saveNotificationSettings(updated);
                }}
              >
                {notifSettings.enabled ? (
                  <FontAwesomeIcon icon={faToggleOn} className="h-7 w-7 text-cyan-400" />
                ) : (
                  <FontAwesomeIcon icon={faToggleOff} className="h-7 w-7 text-slate-500" />
                )}
              </button>
            </div>

            {/* Permission status + re-request */}
            <div className="px-5 py-3 border-b border-gray-200 dark:border-slate-700/30 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-slate-400">Browser permission:</span>
                {notifPermission === 'granted' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
                    <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" /> Granted
                  </span>
                )}
                {notifPermission === 'denied' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-red-500/20 text-red-700 dark:text-red-400 border border-red-500/30">
                    <FontAwesomeIcon icon={faCircleExclamation} className="h-3 w-3" /> Denied
                  </span>
                )}
                {notifPermission === 'default' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/30">
                    Not asked yet
                  </span>
                )}
                {notifPermission === 'unsupported' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-slate-500/20 text-slate-600 dark:text-slate-400 border border-slate-500/30">
                    Not supported
                  </span>
                )}
              </div>
              {notifPermission !== 'unsupported' && notifPermission !== 'granted' && (
                <button
                  onClick={async () => {
                    await requestNotificationPermission();
                    setNotifPermission(getPermissionState());
                  }}
                  className="px-3 py-1.5 text-xs font-medium bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <FontAwesomeIcon icon={faBell} className="h-3 w-3" />
                  {notifPermission === 'denied' ? 'Re-request Permission' : 'Request Permission'}
                </button>
              )}
              {notifPermission === 'denied' && (
                <p className="text-[10px] text-red-400 dark:text-red-500 ml-2">
                  Permission was denied. You may need to allow it in your browser's site settings.
                </p>
              )}
            </div>

            {/* Per-mode toggles */}
            {notifSettings.enabled && (
              <div className="p-5 space-y-2">
                <p className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">Per-mode toggles</p>
                {(Object.keys(NOTIFICATION_MODE_LABELS) as NotificationMode[]).map((mode) => (
                  <div key={mode} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-700 dark:text-slate-300">{NOTIFICATION_MODE_LABELS[mode]}</span>
                    <button
                      onClick={() => {
                        const updated: NotificationSettings = {
                          ...notifSettings,
                          modes: { ...notifSettings.modes, [mode]: !notifSettings.modes[mode] }
};
                        setNotifSettings(updated);
                        saveNotificationSettings(updated);
                      }}
                    >
                      {notifSettings.modes[mode] ? (
                        <FontAwesomeIcon icon={faToggleOn} className="h-6 w-6 text-cyan-400" />
                      ) : (
                        <FontAwesomeIcon icon={faToggleOff} className="h-6 w-6 text-slate-500" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!notifSettings.enabled && (
              <div className="p-5">
                <p className="text-sm text-gray-400 dark:text-slate-500">Enable notifications to control per-mode settings.</p>
              </div>
            )}
          </div>
        </>)}

        {activeSection === 'profile' && (
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <div className="p-5 border-b border-gray-200 dark:border-slate-700/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-orange-600/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCodeCommit} className="h-5 w-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">User Aliases</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Map duplicate Git authors to a single canonical name (e.g. "Jane D" → "Jane Doe")</p>
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Existing aliases */}
              {gitAliases.length > 0 && (
                <div className="space-y-2">
                  {gitAliases.map((alias, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-white/80 dark:bg-slate-900/50 rounded-lg px-3 py-2">
                      <span className="text-[10px] text-gray-500 dark:text-slate-400 font-mono flex-1 truncate">{alias.name}</span>
                      <span className="text-[10px] text-gray-400 dark:text-slate-600">→</span>
                      <span className="text-xs text-emerald-700 dark:text-emerald-400 font-mono flex-1 truncate">{alias.canonicalName}</span>
                      <button
                        onClick={() => {
                          const updated = gitAliases.filter((_, i) => i !== idx);
                          setGitAliases(updated);
                          storeGitAliases(updated);
                        }}
                        className="p-1 hover:bg-red-500/20 rounded transition-colors"
                      >
                        <FontAwesomeIcon icon={faMinus} className="h-3 w-3 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {gitAliases.length === 0 && (
                <p className="text-xs text-slate-500">No aliases configured. Git authors like "Jane D" and "Jane Doe" will be counted as separate people.</p>
              )}

              {/* Add new alias */}
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-1">Alias (variant name)</label>
                  <input
                    type="text"
                    value={newAliasName}
                    onChange={(e) => setNewAliasName(e.target.value)}
                    placeholder="e.g. Jane D"
                    className="w-full px-3 py-2 bg-white/90 dark:bg-slate-900/50 border border-gray-300 dark:border-slate-700/50 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <span className="text-gray-400 dark:text-slate-600 pb-2">→</span>
                <div className="flex-1">
                  <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-1">Canonical name</label>
                  <input
                    type="text"
                    value={newAliasCanonical}
                    onChange={(e) => setNewAliasCanonical(e.target.value)}
                    placeholder="e.g. Jane Doe"
                    className="w-full px-3 py-2 bg-white/90 dark:bg-slate-900/50 border border-gray-300 dark:border-slate-700/50 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 focus:outline-none focus:border-blue-500/50"
                  />
                </div>
                <button
                  onClick={() => {
                    if (newAliasName.trim() && newAliasCanonical.trim()) {
                      const updated = [...gitAliases, { name: newAliasName.trim(), canonicalName: newAliasCanonical.trim() }];
                      setGitAliases(updated);
                      storeGitAliases(updated);
                      setNewAliasName('');
                      setNewAliasCanonical('');
                    }
                  }}
                  disabled={!newAliasName.trim() || !newAliasCanonical.trim()}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-200 dark:disabled:bg-slate-700 disabled:text-gray-400 dark:disabled:text-slate-500 text-white text-sm rounded-lg transition-all flex items-center gap-1"
                >
                  <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                  Add
                </button>
              </div>

              <p className="text-[10px] text-gray-400 dark:text-slate-600">
                Aliases are case-insensitive. Bot accounts like "Snyk Bot" can also be aliased to be excluded or grouped.
              </p>
            </div>
          </div>
        )}

        {activeSection === 'data' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Data & Backup</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Export, import, and manage your VeriGit profile</p>
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-5 space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Export / Import Profile</h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                Transfer your entire VeriGit session to another device — credentials, repos, targets, status categories, theme, and aliases. The export file is AES-256 encrypted with your chosen password.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowExportModal(true)}
                  className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-colors bg-blue-600 hover:bg-blue-500 text-white"
                >
                  <FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-3.5 w-3.5" />
                  Export Profile
                </button>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-colors bg-gray-100 dark:bg-slate-700/50 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 border border-gray-300 dark:border-slate-600"
                >
                  <FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-3.5 w-3.5" />
                  Import Profile
                </button>
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-slate-700/50 pt-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Export / Import Indicators</h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                Share your indicator setup with team members. Exports custom indicators, code customizations, and enabled/disabled states as a plain JSON file.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={async () => {
                    try {
                      const { downloadIndicatorExport } = await import('@/services/profile-export');
                      await downloadIndicatorExport();
                    } catch (err) {
                      console.error('Indicator export failed:', err);
                    }
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-colors bg-violet-600 hover:bg-violet-500 text-white"
                >
                  <FontAwesomeIcon icon={faFileCode} className="h-3.5 w-3.5" />
                  Export Indicators
                </button>
                <label className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-colors bg-gray-100 dark:bg-slate-700/50 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 border border-gray-300 dark:border-slate-600 cursor-pointer">
                  <FontAwesomeIcon icon={faFileCode} className="h-3.5 w-3.5" />
                  Import Indicators
                  <input
                    type="file"
                    accept=".json"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const { importIndicators } = await import('@/services/profile-export');
                        const result = await importIndicators(file);
                        setImportResult({ success: true, items: result.imported });
                        setShowImportResultModal(true);
                      } catch (err) {
                        setImportResult({ success: false, error: (err as Error).message });
                        setShowImportResultModal(true);
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
          {/* About */}
          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <img src="/verigit.png" alt="VeriGit" className="h-8" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">VeriGit</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">Team analytics for every kind of team</p>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Credentials are stored locally and never leave your device. API tokens are used for read-only access.
            </p>
          </div>
        </>)}

        {activeSection === 'ai-settings' && (<>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-1">AI Settings</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Configure your own AI API key for changelog enrichment (BYOK — Bring Your Own Key). Your key is stored locally and never sent to VeriGit servers.</p>

          <div className="bg-white/95 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden divide-y divide-gray-100 dark:divide-slate-700/50">
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-widest block mb-1.5">Provider</label>
                <select value={aiSettingsDraft.provider || 'openai'} onChange={e => setAiSettingsDraft(s => ({ ...s, provider: e.target.value as AiSettings['provider'], model: undefined }))}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="custom">Custom (OpenAI-compatible)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-widest block mb-1.5">API Key</label>
                <input type="password" value={aiSettingsDraft.apiKey || ''} onChange={e => setAiSettingsDraft(s => ({ ...s, apiKey: e.target.value }))}
                  placeholder={aiSettingsDraft.provider === 'anthropic' ? 'sk-ant-...' : 'sk-...'}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-mono" />
              </div>
              {aiSettingsDraft.provider !== 'custom' && (
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-widest block mb-1.5">Model</label>
                  <select value={aiSettingsDraft.model || ''} onChange={e => setAiSettingsDraft(s => ({ ...s, model: e.target.value }))}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white">
                    <option value="">Default</option>
                    {AI_MODEL_OPTIONS[aiSettingsDraft.provider || 'openai'].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              )}
              {aiSettingsDraft.provider === 'custom' && (
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-widest block mb-1.5">Endpoint URL</label>
                  <input type="url" value={aiSettingsDraft.customEndpoint || ''} onChange={e => setAiSettingsDraft(s => ({ ...s, customEndpoint: e.target.value }))}
                    placeholder="https://your-api/v1/chat/completions"
                    className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white font-mono" />
                </div>
              )}
            </div>
            <div className="px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {aiSettingsSaved && (
                  <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" />Key saved
                  </span>
                )}
                {aiSaveSuccess && <span className="text-xs text-emerald-500">Saved!</span>}
              </div>
              <div className="flex items-center gap-2">
                {aiSettingsSaved && (
                  <button onClick={handleClearAiSettings} className="text-xs px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">Remove key</button>
                )}
                <button onClick={handleSaveAiSettings} disabled={aiSaving || !aiSettingsDraft.apiKey}
                  className="text-xs px-4 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {aiSaving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </>)}
          </div>
        </main>
      </div>

      {/* Indicator Code Editor Modal */}
      {showIndicatorEditor && (
        <IndicatorConfigEditor onClose={() => setShowIndicatorEditor(false)} />
      )}

      {/* Export Profile Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4" onClick={() => { setShowExportModal(false); setExportPassword(''); setExportConfirmPassword(''); setExportImportError(null); }}>
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600/50 rounded-xl w-full max-w-md p-6 space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FontAwesomeIcon icon={faShieldHalved} className="h-5 w-5 text-blue-500" />
                Export Profile
              </h3>
              <button onClick={() => { setShowExportModal(false); setExportPassword(''); setExportConfirmPassword(''); setExportImportError(null); }} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-gray-500 dark:text-slate-400" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Your profile will be encrypted with AES-256-GCM using the password you set below. This includes all credentials (API tokens), repos, targets, and preferences.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1 block">Password</label>
                <input
                  type="password"
                  value={exportPassword}
                  onChange={(e) => setExportPassword(e.target.value)}
                  placeholder="Enter encryption password..."
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1 block">Confirm Password</label>
                <input
                  type="password"
                  value={exportConfirmPassword}
                  onChange={(e) => setExportConfirmPassword(e.target.value)}
                  placeholder="Confirm password..."
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
              </div>
            </div>
            {exportImportError && (
              <div className="flex items-center gap-2 text-xs text-red-500 dark:text-red-400">
                <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 flex-shrink-0" />
                {exportImportError}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowExportModal(false); setExportPassword(''); setExportConfirmPassword(''); setExportImportError(null); }}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={!exportPassword || exportPassword.length < 4 || exportPassword !== exportConfirmPassword || exportImportLoading}
                onClick={async () => {
                  setExportImportError(null);
                  setExportImportLoading(true);
                  try {
                    const { encryptAndDownloadProfile } = await import('@/services/profile-export');
                    await encryptAndDownloadProfile(exportPassword);
                    setShowExportModal(false);
                    setExportPassword('');
                    setExportConfirmPassword('');
                  } catch (err) {
                    setExportImportError((err as Error).message);
                  } finally {
                    setExportImportLoading(false);
                  }
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {exportImportLoading ? <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" /> : <FontAwesomeIcon icon={faShieldHalved} className="h-3.5 w-3.5" />}
                {exportImportLoading ? 'Encrypting...' : 'Export'}
              </button>
            </div>
            {exportPassword && exportConfirmPassword && exportPassword !== exportConfirmPassword && (
              <p className="text-[10px] text-amber-500">Passwords do not match</p>
            )}
            {exportPassword && exportPassword.length > 0 && exportPassword.length < 4 && (
              <p className="text-[10px] text-amber-500">Password must be at least 4 characters</p>
            )}
          </div>
        </div>
      )}

      {/* Import Profile Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4" onClick={() => { setShowImportModal(false); setImportPassword(''); setImportFile(null); setExportImportError(null); }}>
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600/50 rounded-xl w-full max-w-md p-6 space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FontAwesomeIcon icon={faShieldHalved} className="h-5 w-5 text-emerald-500" />
                Import Profile
              </h3>
              <button onClick={() => { setShowImportModal(false); setImportPassword(''); setImportFile(null); setExportImportError(null); }} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-gray-500 dark:text-slate-400" />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Select a <code className="text-blue-500">.verigit</code> profile export file and enter the password used during export. This will overwrite your current settings.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1 block">Profile File</label>
                <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-sm cursor-pointer hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors">
                  <span className={importFile ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-slate-500'}>
                    {importFile ? importFile.name : 'Choose .verigit file...'}
                  </span>
                  <input
                    type="file"
                    accept=".verigit,.json"
                    className="hidden"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-slate-300 mb-1 block">Password</label>
                <input
                  type="password"
                  value={importPassword}
                  onChange={(e) => setImportPassword(e.target.value)}
                  placeholder="Enter decryption password..."
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-slate-700/50 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
              </div>
            </div>
            {exportImportError && (
              <div className="flex items-center gap-2 text-xs text-red-500 dark:text-red-400">
                <FontAwesomeIcon icon={faCircleExclamation} className="h-3.5 w-3.5 flex-shrink-0" />
                {exportImportError}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowImportModal(false); setImportPassword(''); setImportFile(null); setExportImportError(null); }}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={!importFile || !importPassword || exportImportLoading}
                onClick={async () => {
                  if (!importFile) return;
                  setExportImportError(null);
                  setExportImportLoading(true);
                  try {
                    const { importProfile } = await import('@/services/profile-export');
                    const result = await importProfile(importFile, importPassword);
                    setShowImportModal(false);
                    setImportPassword('');
                    setImportFile(null);
                    setImportResult({ success: true, items: result.imported });
                    setShowImportResultModal(true);
                  } catch (err) {
                    setExportImportError((err as Error).message);
                  } finally {
                    setExportImportLoading(false);
                  }
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {exportImportLoading ? <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" /> : <FontAwesomeIcon icon={faShieldHalved} className="h-3.5 w-3.5" />}
                {exportImportLoading ? 'Decrypting...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Result Modal */}
      {showImportResultModal && importResult && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4" onClick={() => setShowImportResultModal(false)}>
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600/50 rounded-xl w-full max-w-sm p-6 space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              {importResult.success ? (
                <FontAwesomeIcon icon={faCircleCheck} className="h-5 w-5 text-emerald-500" />
              ) : (
                <FontAwesomeIcon icon={faCircleExclamation} className="h-5 w-5 text-red-500" />
              )}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {importResult.success ? 'Import Successful' : 'Import Failed'}
              </h3>
            </div>
            {importResult.success && importResult.items && (
              <div className="space-y-1">
                <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">The following items were imported:</p>
                {importResult.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-700 dark:text-slate-300">
                    <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3 text-emerald-500 flex-shrink-0" />
                    {item}
                  </div>
                ))}
                <p className="text-[10px] text-amber-500 dark:text-amber-400 mt-3">
                  Reload the page to see all changes take effect.
                </p>
              </div>
            )}
            {!importResult.success && importResult.error && (
              <p className="text-xs text-red-500 dark:text-red-400">{importResult.error}</p>
            )}
            <div className="flex justify-end pt-2">
              <button
                onClick={() => {
                  setShowImportResultModal(false);
                  if (importResult?.success) window.location.reload();
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors"
              >
                {importResult.success ? 'Reload Page' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Token Expiry Modal */}
      <TokenExpiryModal />
    </div>
  );
}

export default SettingsView;
